// @ts-nocheck
import React from 'react';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import wordDocumentService from './services/wordDocumentService';
import { updateCharterCrew, loadCharterCrew, getAllBookings, loadChartererData, saveChartererData } from './services/apiService';
import { useSignatureTouch } from './utils/useSignatureTouch';
import PizZip from 'pizzip';
import Docxtemplater from 'docxtemplater';
import { saveAs } from 'file-saver';
import { useAutoRefresh } from './hooks/useAutoRefresh';
import { codeMatches } from './utils/searchUtils';
import { toLatin, latinId, normId, normName, idKeys, samePersonStrict, unifyCrew } from './utils/identity';
import { formatDateEU } from './utils/dateFormat';
import CountrySelect from './components/CountrySelect';
import { isCompanyCharterer, companyChartererPatch, crewWithoutCharterer, stripIndividualFields, INDIVIDUAL_KEYS } from './utils/chartererType';
import './charter-theme.css';
import { I18N, LANG_MAP, flagImg } from './shared-components';
import { HELP_TEXTS } from './CharterGuidePage';

// Declare jsPDF as global
declare global {
  interface Window {
    jspdf: any;
  }
}

/* Local HELP_TEXTS removed — now imported from CharterGuidePage.tsx
const _UNUSED_HELP_TEXTS_LOCAL: Record<string, any> = {
  el: {
    helpButton: "❓ Οδηγίες",
    title: "📋 ΟΔΗΓΙΕΣ — Ναυλοσύμφωνο & Λίστα Πληρώματος",
    welcome: "⚓ Καλωσήρθατε στην Tailwind Yachting!",
    intro: "Σας ευχαριστούμε που επιλέξατε εμάς για τις διακοπές σας στην Ελλάδα. Έχετε 3 επιλογές για να συμπληρώσετε το Ναυλοσύμφωνο και τη Λίστα Πληρώματος.",
    option1Title: "ΕΠΙΛΟΓΗ 1 — Download & Upload",
    option1Body: "Συμπληρώστε στοιχεία + υπογραφή online → Πατήστε Αποθήκευση → Κατεβάστε Charter Party DOCX & Crew List DOCX → Συμπληρώστε σε Word/PDF/χειρόγραφα → Ανεβάστε όλα + δίπλωμα Σκίπερ → Υποβολή.",
    option2Title: "ΕΠΙΛΟΓΗ 2 — Online με Links (προτεινόμενη)",
    option2Body: "Συμπληρώστε στοιχεία + υπογραφή → Αποθήκευση → Στείλτε links σε Σκίπερ & κάθε μέλος πληρώματος → Ανεβάστε δίπλωμα Σκίπερ → Υποβολή.",
    option3Title: "ΕΠΙΛΟΓΗ 3 — Όλα Online",
    option3Body: "Συμπληρώστε εσείς όλα τα στοιχεία (δικά σας, σκίπερ, μελών) → Για υπογραφή σκίπερ, στείλτε link ή συμπληρώστε εσείς (χωρίς υπογραφή) → Ανεβάστε δίπλωμα → Υποβολή.",
    warning: "⚠️ Πάντα κάντε Αποθήκευση πριν κατεβάσετε ή υποβάλετε.",
    contact: "📞 Βοήθεια: info@tailwindyachting.com",
    safe: "🌊 Καλό ταξίδι!",
    closeBtn: "Κλείσιμο"
  },
  en: {
    helpButton: "❓ Instructions",
    title: "📋 INSTRUCTIONS — Charter Party & Crew List",
    welcome: "⚓ Welcome to Tailwind Yachting!",
    intro: "Thank you for choosing us for your holidays in Greece. You have 3 options to complete the Charter Party and Crew List.",
    option1Title: "OPTION 1 — Download & Upload",
    option1Body: "Fill in your details + signature online → Click Save → Download Charter Party DOCX & Crew List DOCX → Complete in Word/PDF/by hand → Upload all + Skipper licence → Submit.",
    option2Title: "OPTION 2 — Online with Links (recommended)",
    option2Body: "Fill in your details + signature → Save → Send links to the Skipper & each crew member → Upload Skipper licence → Submit.",
    option3Title: "OPTION 3 — All Online",
    option3Body: "You fill in all details (yours, skipper's, crew's) → For skipper's signature, send a link or fill in yourself (no signature) → Upload licence → Submit.",
    warning: "⚠️ Always Save before downloading or submitting.",
    contact: "📞 Help: info@tailwindyachting.com",
    safe: "🌊 Have a great trip!",
    closeBtn: "Close"
  },
  it: {
    helpButton: "❓ Istruzioni",
    title: "📋 ISTRUZIONI — Contratto di Noleggio & Lista Equipaggio",
    welcome: "⚓ Benvenuti a Tailwind Yachting!",
    intro: "Grazie per aver scelto noi per le vostre vacanze in Grecia. Avete 3 opzioni per completare il Contratto di Noleggio (Charter Party) e la Lista Equipaggio (Crew List).",
    option1Title: "OPZIONE 1 — Scarica & Carica",
    option1Body: "Inserite i vostri dati + firma online → Cliccate Salva → Scaricate Charter Party DOCX e Crew List DOCX → Compilate in Word/PDF/a mano → Caricate tutto + patente nautica dello Skipper → Inviate.",
    option2Title: "OPZIONE 2 — Online con Link (consigliata)",
    option2Body: "Inserite dati + firma → Salva → Inviate link allo Skipper e a ciascun membro dell'equipaggio → Caricate la patente dello Skipper → Inviate.",
    option3Title: "OPZIONE 3 — Tutto Online",
    option3Body: "Compilate voi tutti i dati (vostri, dello skipper, dell'equipaggio) → Per la firma dello skipper, inviate un link oppure compilate voi (senza firma) → Caricate la patente → Inviate.",
    warning: "⚠️ Cliccate sempre Salva prima di scaricare o inviare.",
    contact: "📞 Aiuto: info@tailwindyachting.com",
    safe: "🌊 Buon viaggio!",
    closeBtn: "Chiudi"
  },
  de: {
    helpButton: "❓ Anleitung",
    title: "📋 ANLEITUNG — Chartervertrag & Crewliste",
    welcome: "⚓ Willkommen bei Tailwind Yachting!",
    intro: "Vielen Dank, dass Sie uns für Ihren Urlaub in Griechenland gewählt haben. Sie haben 3 Möglichkeiten, den Chartervertrag (Charter Party) und die Crewliste (Crew List) auszufüllen.",
    option1Title: "OPTION 1 — Herunterladen & Hochladen",
    option1Body: "Ihre Daten + Unterschrift online ausfüllen → Speichern klicken → Charter Party DOCX & Crew List DOCX herunterladen → In Word/PDF/handschriftlich ausfüllen → Alles + Skipper-Patent hochladen → Absenden.",
    option2Title: "OPTION 2 — Online mit Links (empfohlen)",
    option2Body: "Daten + Unterschrift ausfüllen → Speichern → Links an Skipper & jedes Crewmitglied senden → Skipper-Patent hochladen → Absenden.",
    option3Title: "OPTION 3 — Alles Online",
    option3Body: "Sie füllen alle Daten selbst aus (Ihre, des Skippers, der Crew) → Für die Unterschrift des Skippers senden Sie einen Link oder füllen selbst aus (ohne Unterschrift) → Patent hochladen → Absenden.",
    warning: "⚠️ Klicken Sie immer Speichern, bevor Sie herunterladen oder absenden.",
    contact: "📞 Hilfe: info@tailwindyachting.com",
    safe: "🌊 Gute Reise!",
    closeBtn: "Schließen"
  },
  ru: {
    helpButton: "❓ Инструкции",
    title: "📋 ИНСТРУКЦИИ — Договор фрахтования & Список экипажа",
    welcome: "⚓ Добро пожаловать в Tailwind Yachting!",
    intro: "Благодарим вас за выбор нашей компании для отдыха в Греции. У вас есть 3 варианта заполнения Договора фрахтования (Charter Party) и Списка экипажа (Crew List).",
    option1Title: "ВАРИАНТ 1 — Скачать и загрузить",
    option1Body: "Заполните свои данные + подпись онлайн → Нажмите Сохранить → Скачайте Charter Party DOCX и Crew List DOCX → Заполните в Word/PDF/от руки → Загрузите всё + удостоверение шкипера → Отправить.",
    option2Title: "ВАРИАНТ 2 — Онлайн со ссылками (рекомендуется)",
    option2Body: "Заполните данные + подпись → Сохранить → Отправьте ссылки шкиперу и каждому члену экипажа → Загрузите удостоверение шкипера → Отправить.",
    option3Title: "ВАРИАНТ 3 — Всё онлайн",
    option3Body: "Вы заполняете все данные сами (свои, шкипера, экипажа) → Для подписи шкипера отправьте ссылку или заполните сами (без подписи) → Загрузите удостоверение → Отправить.",
    warning: "⚠️ Всегда нажимайте Сохранить перед скачиванием или отправкой.",
    contact: "📞 Помощь: info@tailwindyachting.com",
    safe: "🌊 Счастливого пути!",
    closeBtn: "Закрыть"
  },
  fr: {
    helpButton: "❓ Instructions",
    title: "📋 INSTRUCTIONS — Contrat d'Affrètement & Liste d'Équipage",
    welcome: "⚓ Bienvenue chez Tailwind Yachting !",
    intro: "Merci de nous avoir choisis pour vos vacances en Grèce. Vous avez 3 options pour compléter le Contrat d'Affrètement (Charter Party) et la Liste d'Équipage (Crew List).",
    option1Title: "OPTION 1 — Télécharger & Envoyer",
    option1Body: "Remplissez vos coordonnées + signature en ligne → Cliquez sur Enregistrer → Téléchargez Charter Party DOCX et Crew List DOCX → Remplissez en Word/PDF/à la main → Envoyez tout + permis du Skipper → Soumettre.",
    option2Title: "OPTION 2 — En ligne avec Liens (recommandée)",
    option2Body: "Remplissez coordonnées + signature → Enregistrer → Envoyez des liens au Skipper et à chaque membre d'équipage → Envoyez le permis du Skipper → Soumettre.",
    option3Title: "OPTION 3 — Tout en ligne",
    option3Body: "Vous remplissez vous-même toutes les données (les vôtres, du skipper, de l'équipage) → Pour la signature du skipper, envoyez un lien ou remplissez vous-même (sans signature) → Envoyez le permis → Soumettre.",
    warning: "⚠️ Cliquez toujours sur Enregistrer avant de télécharger ou de soumettre.",
    contact: "📞 Aide : info@tailwindyachting.com",
    safe: "🌊 Bon voyage !",
    closeBtn: "Fermer"
  },
  ro: {
    helpButton: "❓ Instrucțiuni",
    title: "📋 INSTRUCȚIUNI — Contract de Navlosire & Lista Echipajului",
    welcome: "⚓ Bine ați venit la Tailwind Yachting!",
    intro: "Vă mulțumim că ne-ați ales pentru vacanța dumneavoastră în Grecia. Aveți 3 opțiuni pentru a completa Contractul de Navlosire (Charter Party) și Lista Echipajului (Crew List).",
    option1Title: "OPȚIUNEA 1 — Descărcare & Încărcare",
    option1Body: "Completați datele dvs. + semnătura online → Apăsați Salvează → Descărcați Charter Party DOCX și Crew List DOCX → Completați în Word/PDF/de mână → Încărcați tot + brevetul de Skipper → Trimite.",
    option2Title: "OPȚIUNEA 2 — Online cu Link-uri (recomandată)",
    option2Body: "Completați datele + semnătura → Salvează → Trimiteți link-uri Skipperului și fiecărui membru al echipajului → Încărcați brevetul de Skipper → Trimite.",
    option3Title: "OPȚIUNEA 3 — Totul Online",
    option3Body: "Completați dvs. toate datele (ale dvs., ale skipperului, ale echipajului) → Pentru semnătura skipperului, trimiteți un link sau completați dvs. (fără semnătură) → Încărcați brevetul → Trimite.",
    warning: "⚠️ Apăsați întotdeauna Salvează înainte de descărcare sau trimitere.",
    contact: "📞 Asistență: info@tailwindyachting.com",
    safe: "🌊 Călătorie plăcută!",
    closeBtn: "Închide"
  },
  pl: {
    helpButton: "❓ Instrukcje",
    title: "📋 INSTRUKCJE — Umowa Czarteru & Lista Załogi",
    welcome: "⚓ Witamy w Tailwind Yachting!",
    intro: "Dziękujemy za wybór naszej firmy na wakacje w Grecji. Masz 3 opcje wypełnienia Umowy Czarteru (Charter Party) i Listy Załogi (Crew List).",
    option1Title: "OPCJA 1 — Pobierz i Wyślij",
    option1Body: "Wypełnij swoje dane + podpis online → Kliknij Zapisz → Pobierz Charter Party DOCX i Crew List DOCX → Wypełnij w Word/PDF/odręcznie → Prześlij wszystko + patent Skippera → Wyślij.",
    option2Title: "OPCJA 2 — Online z Linkami (zalecane)",
    option2Body: "Wypełnij dane + podpis → Zapisz → Wyślij linki Skipperowi i każdemu członkowi załogi → Prześlij patent Skippera → Wyślij.",
    option3Title: "OPCJA 3 — Wszystko Online",
    option3Body: "Sam wypełniasz wszystkie dane (swoje, skippera, załogi) → Po podpis skippera wyślij link lub wypełnij sam (bez podpisu) → Prześlij patent → Wyślij.",
    warning: "⚠️ Zawsze kliknij Zapisz przed pobraniem lub wysłaniem.",
    contact: "📞 Pomoc: info@tailwindyachting.com",
    safe: "🌊 Udanej podróży!",
    closeBtn: "Zamknij"
  },
  he: {
    helpButton: "❓ הוראות",
    title: "📋 הוראות — חוזה שכירת יאכטה ורשימת צוות",
    welcome: "⚓ ברוכים הבאים ל-Tailwind Yachting!",
    intro: "תודה שבחרתם בנו לחופשתכם ביוון. ישנן 3 אפשרויות למילוי חוזה שכירת היאכטה (Charter Party) ורשימת הצוות (Crew List).",
    option1Title: "אפשרות 1 — הורדה והעלאה",
    option1Body: "מלאו את פרטיכם + חתימה אונליין → לחצו שמור → הורידו Charter Party DOCX ו-Crew List DOCX → מלאו ב-Word/PDF/בכתב יד → העלו הכל + רישיון הסקיפר → שלחו.",
    option2Title: "אפשרות 2 — אונליין עם קישורים (מומלץ)",
    option2Body: "מלאו פרטים + חתימה → שמור → שלחו קישורים לסקיפר ולכל איש צוות → העלו רישיון הסקיפר → שלחו.",
    option3Title: "אפשרות 3 — הכל אונליין",
    option3Body: "אתם ממלאים את כל הפרטים (שלכם, של הסקיפר, של הצוות) → לחתימת הסקיפר, שלחו קישור או מלאו בעצמכם (ללא חתימה) → העלו רישיון → שלחו.",
    warning: "⚠️ לחצו תמיד שמור לפני הורדה או שליחה.",
    contact: "📞 עזרה: info@tailwindyachting.com",
    safe: "🌊 נסיעה טובה!",
    closeBtn: "סגור"
  },
  es: {
    helpButton: "❓ Instrucciones",
    title: "📋 INSTRUCCIONES — Contrato de Fletamento & Lista de Tripulación",
    welcome: "⚓ ¡Bienvenidos a Tailwind Yachting!",
    intro: "Gracias por elegirnos para sus vacaciones en Grecia. Tiene 3 opciones para completar el Contrato de Fletamento (Charter Party) y la Lista de Tripulación (Crew List).",
    option1Title: "OPCIÓN 1 — Descargar y Subir",
    option1Body: "Complete sus datos + firma online → Pulse Guardar → Descargue Charter Party DOCX y Crew List DOCX → Complete en Word/PDF/a mano → Suba todo + título del Patrón → Enviar.",
    option2Title: "OPCIÓN 2 — Online con Enlaces (recomendada)",
    option2Body: "Complete datos + firma → Guardar → Envíe enlaces al Patrón y a cada miembro de la tripulación → Suba el título del Patrón → Enviar.",
    option3Title: "OPCIÓN 3 — Todo Online",
    option3Body: "Complete usted mismo todos los datos (suyos, del patrón, de la tripulación) → Para la firma del patrón, envíe un enlace o complete usted mismo (sin firma) → Suba el título → Enviar.",
    warning: "⚠️ Pulse siempre Guardar antes de descargar o enviar.",
    contact: "📞 Ayuda: info@tailwindyachting.com",
    safe: "🌊 ¡Buen viaje!",
    closeBtn: "Cerrar"
  }
};
*/

// === Local translations for skipper popup + sign-first warning ===
// Lives here (not in shared-components.js) per project rule. Add new langs by extending these maps.

// WhatsApp invite μήνυμα ανά γλώσσα (ίδια λογική με το email invite).
const WA_MSG: Record<string, { hello: string; body: string }> = {
  el: { hello: 'Γεια σου', body: 'συμπλήρωσε τα στοιχεία σου για τον ναύλο μας:' },
  en: { hello: 'Hello',    body: 'please complete your details for our charter:' },
  it: { hello: 'Ciao',     body: 'completa i tuoi dati per il nostro charter:' },
  de: { hello: 'Hallo',    body: 'bitte vervollständige deine Daten für unseren Charter:' },
  ru: { hello: 'Здравствуйте', body: 'пожалуйста, заполните свои данные для нашего чартера:' },
  fr: { hello: 'Bonjour',  body: 'merci de compléter vos informations pour notre charter:' },
  ro: { hello: 'Bună',     body: 'te rugăm să îți completezi datele pentru charterul nostru:' },
  pl: { hello: 'Cześć',    body: 'uzupełnij swoje dane do naszego czarteru:' },
  he: { hello: 'שלום',     body: 'אנא מלא את הפרטים שלך עבור הצ׳רטר שלנו:' },
  es: { hello: 'Hola',     body: 'por favor completa tus datos para nuestro chárter:' },
};

// Δίπλωμα skipper: OPTIONAL εδώ — ⚠ ένδειξη «εκκρεμεί» αν λείπει (required μόνο στο invitation).
// S4b: dropdown «Ο skipper είναι» (10 γλώσσες).
const SKIPPER_MODE: Record<string, any> = {
  el: { label:'Ο skipper είναι', me:'Εγώ (ο ναυλωτής)', crew:'Μέλος του πληρώματος', other:'Άλλο πρόσωπο', invite:'Θα σταλεί invitation', youAreSkipper:'Skipper: Εσείς', fromRegistration:'από την εγγραφή', selectMember:'Επιλέξτε μέλος', gotoInvite:'Στείλτε invitation στον skipper (ενότητα «Invite Crew/Skipper» πιο κάτω).' },
  en: { label:'The skipper is', me:'Me (the charterer)', crew:'A crew member', other:'Another person', invite:'An invitation will be sent', youAreSkipper:'Skipper: You', fromRegistration:'from registration', selectMember:'Select member', gotoInvite:'Send an invitation to the skipper (the “Invite Crew/Skipper” section below).' },
  it: { label:'Lo skipper è', me:'Io (il noleggiatore)', crew:'Un membro dell’equipaggio', other:'Un’altra persona', invite:'Verrà inviato un invito', youAreSkipper:'Skipper: Tu', fromRegistration:'dalla registrazione', selectMember:'Seleziona membro', gotoInvite:'Invia un invito allo skipper (sezione “Invite Crew/Skipper” sotto).' },
  de: { label:'Der Skipper ist', me:'Ich (der Charterer)', crew:'Ein Crewmitglied', other:'Eine andere Person', invite:'Eine Einladung wird gesendet', youAreSkipper:'Skipper: Sie', fromRegistration:'aus der Registrierung', selectMember:'Mitglied wählen', gotoInvite:'Senden Sie dem Skipper eine Einladung (Abschnitt „Invite Crew/Skipper“ unten).' },
  ru: { label:'Шкипер —', me:'Я (фрахтователь)', crew:'Член экипажа', other:'Другой человек', invite:'Будет отправлено приглашение', youAreSkipper:'Шкипер: Вы', fromRegistration:'из регистрации', selectMember:'Выберите члена', gotoInvite:'Отправьте приглашение шкиперу (раздел «Invite Crew/Skipper» ниже).' },
  fr: { label:'Le skipper est', me:'Moi (l’affréteur)', crew:'Un membre d’équipage', other:'Une autre personne', invite:'Une invitation sera envoyée', youAreSkipper:'Skipper : Vous', fromRegistration:'depuis l’inscription', selectMember:'Choisir un membre', gotoInvite:'Envoyez une invitation au skipper (section « Invite Crew/Skipper » ci-dessous).' },
  ro: { label:'Skipperul este', me:'Eu (navlositorul)', crew:'Un membru al echipajului', other:'O altă persoană', invite:'Se va trimite o invitație', youAreSkipper:'Skipper: Dvs.', fromRegistration:'din înregistrare', selectMember:'Selectați membru', gotoInvite:'Trimiteți o invitație skipperului (secțiunea „Invite Crew/Skipper” mai jos).' },
  pl: { label:'Skipperem jest', me:'Ja (czarterujący)', crew:'Członek załogi', other:'Inna osoba', invite:'Zostanie wysłane zaproszenie', youAreSkipper:'Skipper: Ty', fromRegistration:'z rejestracji', selectMember:'Wybierz członka', gotoInvite:'Wyślij zaproszenie do skippera (sekcja „Invite Crew/Skipper” poniżej).' },
  he: { label:'הסקיפר הוא', me:'אני (השוכר)', crew:'איש צוות', other:'אדם אחר', invite:'תישלח הזמנה', youAreSkipper:'סקיפר: אתה', fromRegistration:'מההרשמה', selectMember:'בחר חבר', gotoInvite:'שלח הזמנה לסקיפר (מקטע “Invite Crew/Skipper” למטה).' },
  es: { label:'El patrón es', me:'Yo (el fletador)', crew:'Un miembro de la tripulación', other:'Otra persona', invite:'Se enviará una invitación', youAreSkipper:'Patrón: Usted', fromRegistration:'del registro', selectMember:'Seleccionar miembro', gotoInvite:'Envíe una invitación al patrón (sección “Invite Crew/Skipper” abajo).' },
};

// S5: Σύνοψη προ-Submit (10 γλώσσες).
const SUMMARY_T: Record<string, any> = {
  el: { title:'Σύνοψη', charterer:'Ναυλωτής', skipper:'Skipper', crew:'Πλήρωμα', documents:'Έγγραφα', signed:'✓ Υπογεγραμμένο', notSigned:'⏳ Εκκρεμεί υπογραφή', licenseOk:'✓ Δίπλωμα', licensePending:'⚠ Δίπλωμα εκκρεμεί', me:'Ο ναυλωτής (εσείς)', viaInvite:'Θα σταλεί invitation', edit:'Διόρθωση', charterPartyScan:'Σάρωση Charter Party', crewListScan:'Σάρωση Crew List', skipperPending:'⏳ Αναμονή στοιχείων skipper' },
  en: { title:'Summary', charterer:'Charterer', skipper:'Skipper', crew:'Crew', documents:'Documents', signed:'✓ Signed', notSigned:'⏳ Signature pending', licenseOk:'✓ License', licensePending:'⚠ License pending', me:'The charterer (you)', viaInvite:'An invitation will be sent', edit:'Edit', charterPartyScan:'Charter Party scan', crewListScan:'Crew List scan', skipperPending:'⏳ Awaiting skipper details' },
  it: { title:'Riepilogo', charterer:'Noleggiatore', skipper:'Skipper', crew:'Equipaggio', documents:'Documenti', signed:'✓ Firmato', notSigned:'⏳ Firma in sospeso', licenseOk:'✓ Licenza', licensePending:'⚠ Licenza in sospeso', me:'Il noleggiatore (tu)', viaInvite:'Verrà inviato un invito', edit:'Modifica', charterPartyScan:'Scansione Charter Party', crewListScan:'Scansione Crew List', skipperPending:'⏳ In attesa dei dati dello skipper' },
  de: { title:'Zusammenfassung', charterer:'Charterer', skipper:'Skipper', crew:'Crew', documents:'Dokumente', signed:'✓ Unterschrieben', notSigned:'⏳ Unterschrift ausstehend', licenseOk:'✓ Lizenz', licensePending:'⚠ Lizenz ausstehend', me:'Der Charterer (Sie)', viaInvite:'Eine Einladung wird gesendet', edit:'Bearbeiten', charterPartyScan:'Charter-Party-Scan', crewListScan:'Crew-List-Scan', skipperPending:'⏳ Warten auf Skipper-Daten' },
  ru: { title:'Сводка', charterer:'Фрахтователь', skipper:'Шкипер', crew:'Экипаж', documents:'Документы', signed:'✓ Подписано', notSigned:'⏳ Ожидается подпись', licenseOk:'✓ Лицензия', licensePending:'⚠ Лицензия ожидается', me:'Фрахтователь (вы)', viaInvite:'Будет отправлено приглашение', edit:'Изменить', charterPartyScan:'Скан Charter Party', crewListScan:'Скан Crew List', skipperPending:'⏳ Ожидание данных шкипера' },
  fr: { title:'Récapitulatif', charterer:'Affréteur', skipper:'Skipper', crew:'Équipage', documents:'Documents', signed:'✓ Signé', notSigned:'⏳ Signature en attente', licenseOk:'✓ Licence', licensePending:'⚠ Licence en attente', me:'L’affréteur (vous)', viaInvite:'Une invitation sera envoyée', edit:'Modifier', charterPartyScan:'Scan Charter Party', crewListScan:'Scan Crew List', skipperPending:'⏳ En attente des données du skipper' },
  ro: { title:'Rezumat', charterer:'Navlositor', skipper:'Skipper', crew:'Echipaj', documents:'Documente', signed:'✓ Semnat', notSigned:'⏳ Semnătură în așteptare', licenseOk:'✓ Licență', licensePending:'⚠ Licență în așteptare', me:'Navlositorul (dvs.)', viaInvite:'Se va trimite o invitație', edit:'Editare', charterPartyScan:'Scanare Charter Party', crewListScan:'Scanare Crew List', skipperPending:'⏳ Se așteaptă datele skipperului' },
  pl: { title:'Podsumowanie', charterer:'Czarterujący', skipper:'Skipper', crew:'Załoga', documents:'Dokumenty', signed:'✓ Podpisano', notSigned:'⏳ Oczekuje podpisu', licenseOk:'✓ Licencja', licensePending:'⚠ Licencja oczekuje', me:'Czarterujący (Ty)', viaInvite:'Zostanie wysłane zaproszenie', edit:'Edytuj', charterPartyScan:'Skan Charter Party', crewListScan:'Skan Crew List', skipperPending:'⏳ Oczekiwanie na dane skippera' },
  he: { title:'סיכום', charterer:'שוכר', skipper:'סקיפר', crew:'צוות', documents:'מסמכים', signed:'✓ נחתם', notSigned:'⏳ ממתין לחתימה', licenseOk:'✓ רישיון', licensePending:'⚠ רישיון ממתין', me:'השוכר (אתה)', viaInvite:'תישלח הזמנה', edit:'עריכה', charterPartyScan:'סריקת Charter Party', crewListScan:'סריקת Crew List', skipperPending:'⏳ ממתין לפרטי הסקיפר' },
  es: { title:'Resumen', charterer:'Fletador', skipper:'Patrón', crew:'Tripulación', documents:'Documentos', signed:'✓ Firmado', notSigned:'⏳ Firma pendiente', licenseOk:'✓ Licencia', licensePending:'⚠ Licencia pendiente', me:'El fletador (usted)', viaInvite:'Se enviará una invitación', edit:'Editar', charterPartyScan:'Escaneo Charter Party', crewListScan:'Escaneo Crew List', skipperPending:'⏳ Esperando los datos del patrón' },
};

// S3.5: header κάρτα (10 γλώσσες).
const HEADER_T: Record<string, any> = {
  el:{vessel:'Σκάφος', departure:'Αναχώρηση', ret:'Επιστροφή'}, en:{vessel:'Vessel', departure:'Departure', ret:'Return'},
  it:{vessel:'Barca', departure:'Partenza', ret:'Ritorno'}, de:{vessel:'Boot', departure:'Abfahrt', ret:'Rückkehr'},
  ru:{vessel:'Судно', departure:'Отправление', ret:'Возвращение'}, fr:{vessel:'Bateau', departure:'Départ', ret:'Retour'},
  ro:{vessel:'Ambarcațiune', departure:'Plecare', ret:'Întoarcere'}, pl:{vessel:'Jacht', departure:'Wypłynięcie', ret:'Powrót'},
  he:{vessel:'כלי שיט', departure:'יציאה', ret:'חזרה'}, es:{vessel:'Barco', departure:'Salida', ret:'Regreso'},
};

// «Φύλο» (10 γλώσσες) — το ca_gender λείπει από το shared I18N.
const GEN_LABEL: Record<string, string> = {
  el:'Φύλο', en:'Gender', it:'Sesso', de:'Geschlecht', ru:'Пол', fr:'Sexe', ro:'Sex', pl:'Płeć', he:'מין', es:'Sexo',
};

// Ένδειξη «ο σκίπερ φαίνεται ίδιος με τον ναυλωτή» (10 γλώσσες).
const SAME_SKIPPER_HINT: Record<string, string> = {
  el:'Ο σκίπερ φαίνεται ίδιος με τον ναυλωτή (ίδιο όνομα και ίδια ταυτότητα). Θα καταχωρηθεί μία φορά.',
  en:'The skipper appears to be the charterer (same name and same ID). They will be listed once.',
  it:'Lo skipper sembra essere il noleggiatore (stesso nome e stesso documento). Sarà indicato una sola volta.',
  de:'Der Skipper scheint der Charterer zu sein (gleicher Name, gleicher Ausweis). Er wird einmal aufgeführt.',
  ru:'Шкипер, судя по всему, — это фрахтователь (то же имя и тот же документ). Он будет указан один раз.',
  fr:'Le skipper semble être l’affréteur (même nom et même pièce d’identité). Il sera inscrit une seule fois.',
  ro:'Skipperul pare a fi navlositorul (același nume și același act de identitate). Va fi trecut o singură dată.',
  pl:'Skipper wygląda na czarterującego (to samo nazwisko i ten sam dokument). Zostanie ujęty raz.',
  he:'נראה שהסקיפר הוא השוכר (אותו שם ואותו מספר זהות). הוא יירשם פעם אחת.',
  es:'El patrón parece ser el fletador (mismo nombre y mismo documento). Se registrará una sola vez.',
};

// «Required» ένδειξη (10 γλώσσες) — μοτίβο Boatjump: label + κόκκινο.
const REQ: Record<string, string> = {
  el: 'Υποχρεωτικό', en: 'Required', it: 'Obbligatorio', de: 'Erforderlich', ru: 'Обязательно',
  fr: 'Obligatoire', ro: 'Obligatoriu', pl: 'Wymagane', he: 'חובה', es: 'Obligatorio',
};

const LICENSE_PENDING: Record<string, string> = {
  el: 'Το δίπλωμα skipper εκκρεμεί (προαιρετικό εδώ — μπορεί να το στείλει ο skipper).',
  en: "Skipper's license pending (optional here — the skipper can send it).",
  it: 'Licenza dello skipper in sospeso (facoltativa qui — può inviarla lo skipper).',
  de: 'Skipperlizenz ausstehend (hier optional — der Skipper kann sie senden).',
  ru: 'Лицензия шкипера ожидается (здесь необязательно — шкипер может её отправить).',
  fr: "Licence du skipper en attente (facultative ici — le skipper peut l'envoyer).",
  ro: 'Licența skipperului în așteptare (opțională aici — o poate trimite skipperul).',
  pl: 'Licencja skippera oczekuje (opcjonalnie tutaj — skipper może ją wysłać).',
  he: 'רישיון הסקיפר ממתין (אופציונלי כאן — הסקיפר יכול לשלוח).',
  es: 'Licencia del patrón pendiente (opcional aquí — el patrón puede enviarla).',
};

// Normalize τηλεφώνου για wa.me: μόνο ψηφία, default country code +30, χωρίς μηδενικά μετά το CC.
const waNumber = (raw: string): string => {
  let d = (raw || '').replace(/\D/g, '');   // μόνο ψηφία (κόβει +, κενά, 00)
  d = d.replace(/^0+/, '');                  // κόψε leading zeros (00 intl / 0 trunk)
  if (!d) return '';
  const CC = '30';
  if (d.startsWith(CC)) {
    const rest = d.slice(CC.length).replace(/^0+/, '');
    return rest ? CC + rest : '';            // μόνο country code χωρίς αριθμό → κενό
  }
  return CC + d;                             // εθνικός αριθμός (69...) → +30
};

const FILL_ONLINE_FIRST: Record<string, string> = {
  el: 'Πρέπει να πάτε στο Online Charter Party να συμπληρώσετε τα στοιχεία σας και να υπογράψετε πρώτα. Μετά μπορείτε να κατεβάσετε το Charter Party για να το δείτε. Δεν χρειάζεται να το ανεβάσετε ξανά.',
  en: 'Please go to the Online Charter Party first to fill in your details and sign. Then you can download the Charter Party to view it. You do not need to upload it again.',
  it: "Vai prima all'Online Charter Party per inserire i tuoi dati e firmare. Poi potrai scaricare il Charter Party per visualizzarlo. Non è necessario ricaricarlo.",
  de: 'Bitte gehen Sie zuerst zum Online Charter Party, um Ihre Daten einzugeben und zu unterschreiben. Dann können Sie das Charter Party herunterladen, um es anzusehen. Sie müssen es nicht erneut hochladen.',
  ru: 'Сначала перейдите в Online Charter Party, чтобы заполнить свои данные и подписать. Затем вы сможете скачать Charter Party для просмотра. Загружать его повторно не нужно.',
  fr: "Veuillez d'abord aller à l'Online Charter Party pour remplir vos coordonnées et signer. Vous pourrez ensuite télécharger le Charter Party pour le consulter. Pas besoin de le retransmettre.",
  ro: 'Vă rugăm să mergeți mai întâi la Online Charter Party pentru a completa datele și a semna. Apoi puteți descărca Charter Party pentru a-l vizualiza. Nu trebuie să-l încărcați din nou.',
  pl: 'Najpierw przejdź do Online Charter Party, aby wypełnić swoje dane i podpisać. Następnie możesz pobrać Charter Party do wglądu. Nie musisz go ponownie przesyłać.',
  he: 'אנא עבור תחילה ל-Online Charter Party כדי למלא את הפרטים ולחתום. לאחר מכן תוכל להוריד את ה-Charter Party לצפייה. אין צורך להעלות אותו שוב.',
  es: 'Por favor, vaya primero al Online Charter Party para completar sus datos y firmar. Luego puede descargar el Charter Party para verlo. No es necesario volver a subirlo.',
};

// 🔥 v6.366 FIX (booking 657): central, pure crew dedup.
// Removes the empty isCharterer "self/skipper" ghost row (created by the save selfEntry
// when sameAsSkipper=true) when a real, fuller namesake exists, and collapses exact dups.
// Name match is whitespace-robust (collapses internal spaces): the double-space
// "Michael  Schertler" is exactly what broke the old name-equality dedup.
// SAFE: never drops a row that has a passport. Preserves order & roles of all other rows.
function dedupeCrew(crew: any[]): any[] {
  if (!Array.isArray(crew)) return crew;
  const normName = (x: any) => String(x == null ? '' : x).replace(/\s+/g, ' ').trim().toLowerCase();
  const has = (v: any) => String(v == null ? '' : v).trim() !== '';
  const richness = (m: any) => (has(m && m.passport) ? 1 : 0) + (has(m && m.dateOfBirth) ? 1 : 0) + (has(m && m.nationality) ? 1 : 0);
  const isEmptyGhost = (m: any) => !!m && m.isCharterer === true && !has(m.passport) && !has(m.dateOfBirth);

  // Pass 1 — drop the empty isCharterer ghost when a same-name+role richer row exists.
  const afterGhost = crew.filter((m: any, i: number) => {
    if (!isEmptyGhost(m)) return true;
    const gName = normName(m.name);
    const gRole = String(m.role || '').toLowerCase();
    if (gName === '') return true; // nothing to match on — keep
    const hasRicherNamesake = crew.some((o: any, j: number) =>
      j !== i && !!o &&
      normName(o.name) === gName &&
      String(o.role || '').toLowerCase() === gRole &&
      (has(o.passport) || has(o.dateOfBirth))
    );
    return !hasRicherNamesake;
  });

  // Pass 2 — collapse exact duplicates. Key: passport if present, else normName|role.
  // Keep the richer of the two; never let an emptier row overwrite one with a passport.
  const byKey: any = new Map();
  const out: any[] = [];
  for (const m of afterGhost) {
    const hasPass = has(m && m.passport);
    const nm = normName(m && m.name);
    if (!hasPass && nm === '') { out.push(m); continue; } // no identity → can't dedup safely
    const key = hasPass
      ? 'p:' + String(m.passport).trim().toLowerCase()
      : 'n:' + nm + '|' + String((m && m.role) || '').toLowerCase();
    if (!byKey.has(key)) {
      byKey.set(key, out.length);
      out.push(m);
    } else {
      const idx = byKey.get(key);
      if (richness(m) > richness(out[idx])) out[idx] = m; // keep richer, preserve position
    }
  }
  return out;
}

// Παλιά ναύλα: τα ΠΛΗΡΗ στοιχεία του charterer συχνά ζουν στο crew record του
// (crewMembers με isCharterer:true — π.χ. DOTAN GAD στον 116), ΟΧΙ στα chartererData πεδία
// (idNumber/dob/gender/nationality κενά). Βρίσκει το crew record του charterer. READ-ONLY.
function findChartererCrewRecord(crew: any[], cd: any): any {
  const list = Array.isArray(crew) ? crew : [];
  if (!list.length) return null;
  // 1) ρητό isCharterer flag
  let rec = list.find((m: any) => m?.isCharterer === true);
  if (rec) return rec;
  // 2) match με email
  const cdEmail = String(cd?.email || '').trim().toLowerCase();
  if (cdEmail) { rec = list.find((m: any) => String(m?.email || '').trim().toLowerCase() === cdEmail); if (rec) return rec; }
  // 3) match με πλήρες όνομα
  const cdName = `${cd?.firstName || ''} ${cd?.lastName || ''}`.replace(/\s+/g, ' ').trim().toLowerCase();
  if (cdName) { rec = list.find((m: any) => String(m?.name || `${m?.firstName || ''} ${m?.lastName || ''}`).replace(/\s+/g, ' ').trim().toLowerCase() === cdName); if (rec) return rec; }
  return null;
}

export default function CharterAgreementPage() {
  const [language, setLanguage] = useState(() => sessionStorage.getItem('yacht_lang') || 'en');
  const [showLangPopup, setShowLangPopup] = useState(false);
  const [showCharterPartyModal, setShowCharterPartyModal] = useState(false);
  const t: any = new Proxy({}, { get: (_target: any, key: string) => { const v = (I18N as any)[language]; if (v && v[key] !== undefined) return v[key]; return (I18N as any).en[key]; } });
  const [bookingData, setBookingData] = useState<any>(null);
  const [showCrewForm, setShowCrewForm] = useState(false);
  const [editingCrewIdx, setEditingCrewIdx] = useState<number | null>(null); // S3: ποιο crew row είναι ανοιχτό για edit
  const [skipperLicense, setSkipperLicense] = useState<string>('');
  const [extraLicenses, setExtraLicenses] = useState<string[]>([]);
  const [charterPartyScan, setCharterPartyScan] = useState<string>('');
  const [crewListScan, setCrewListScan] = useState<string>('');
  const [crewMembers, setCrewMembers] = useState([
    { name: '', passport: '', dateOfBirth: '', nationality: '', role: 'passenger' }
  ]);

  // Phase 6: Crew invitations state
  const [invitations, setInvitations] = useState<any[]>([]);
  const [inviteForm, setInviteForm] = useState({ firstName: '', lastName: '', email: '', phone: '+30 ', role: 'crew' });
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteMsg, setInviteMsg] = useState('');

  // 🔥 Auto-refresh: Track last update time
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Admin opener + edit-window state (FEATURE: admin can load any booking; charterer can edit until 1 day before check-in)
  const [adminBookingInput, setAdminBookingInput] = useState('');
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState<any>(null);

  // Charterer Details Form (online submission by charterer)
  const [chartererData, setChartererData] = useState({
    firstName: '',
    lastName: '',
    address: '',
    idType: 'id',
    idNumber: '',
    taxNumber: '',
    taxOffice: '',
    tel: '',
    email: '',
    dob: '',
    gender: '',
    nationality: '',
    signature: '',
    isAlsoSkipper: false,
    skipperName: '',
    skipperSignature: '',
    chartererSignedAt: '',
    skipperSignedAt: '',
    // Skipper extended fields (Crew List DOCX requirements). Required: name + signature only.
    skipperFirstName: '',
    skipperLastName: '',
    skipperDob: '',
    skipperIdType: 'passport',
    skipperIdNumber: '',
    skipperGen: '',
    skipperNationality: '',
    skipperPhone: '',
    skipperEmail: '',
    skipperAddress: '',
    skipperTaxNumber: '',
    skipperTaxOffice: ''
  });
  const [showChartererForm, setShowChartererForm] = useState(false);

  // Canvas refs for signatures
  const chartererCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const skipperCanvasRef = useRef<HTMLCanvasElement | null>(null);
  useSignatureTouch(chartererCanvasRef);
  useSignatureTouch(skipperCanvasRef);
  const [isDrawingCharterer, setIsDrawingCharterer] = useState(false);
  const [isDrawingSkipper, setIsDrawingSkipper] = useState(false);

  const [skipperMode, setSkipperMode] = useState<'me' | 'crew' | 'other' | 'invite'>('other'); // S4b
  const skipperModeInit = useRef(false);
  // v6.473 — ΡΗΤΗ ΠΡΟΘΕΣΗ διαγραφής σκίπερ: γίνεται true ΜΟΝΟ όταν ο χρήστης αδειάσει ο
  // ίδιος πεδίο σκίπερ (editSkipper). Ποτέ από φόρτωση/prefill/κενό state — γι' αυτό ναύλοι
  // με γεμάτο flat και κενό Agreement δεν κινδυνεύουν να καθαριστούν κατά λάθος.
  const skipperUserClearedRef = useRef(false);

  // FEATURE: dynamic lock. ADMIN/TECHNICAL → πάντα editable (πριν/κατά/μετά).
  // charterer/broker → editable αυστηρά ΠΡΙΝ charterStartDate 00:00 (local
  // μεσάνυχτα μέρας ναύλου). Από εκεί και μετά PERMANENT readonly.
  // Ανεξάρτητο submit — resubmit επιτρέπεται μέχρι το cutoff.
  // Admin detection: state from useEffect + direct sessionStorage/localStorage
  // fallback (covers first-render race + broker/owner login flows where
  // currentEmployee hasn't been hydrated yet).
  // AUTO-REFRESH GUARD: το 5λεπτο poll (loadCrewData) έγραφε πάνω από ό,τι πληκτρολογούσε
  // ο χρήστης. Τα dirty refs σηκώνονται ΜΟΝΟ από πραγματικό DOM input (onChangeCapture στα
  // δύο containers + stopDrawing για την υπογραφή) — ΟΧΙ από τα programmatic effects/mirrors,
  // που παράγουν state από server data και δεν πρέπει να μπλοκάρουν το refresh.
  const chartererDirtyRef = useRef(false);
  const crewDirtyRef = useRef(false);
  // Το loadCrewData είναι useCallback([bookingCode]) → κλείνει πάνω σε stale bookingData,
  // άρα ΔΕΝ μπορεί να καλέσει canEdit()/canEditCrew() απευθείας. Τα κρατάμε σε refs.
  const canEditRef = useRef(true);
  const canEditCrewRef = useRef(true);

  const isAdmin = () => {
    if (isAdminMode) return true;
    if (currentEmployee && ['ADMIN', 'TECHNICAL', 'OWNER'].includes(currentEmployee.role)) return true;
    try {
      const stored = sessionStorage.getItem('auth_current_user');
      if (stored) {
        const u = JSON.parse(stored);
        if (u && ['ADMIN', 'TECHNICAL', 'OWNER'].includes(u.role)) return true;
      }
      const empCode = localStorage.getItem('employeeCode') || '';
      if (/^(ADMIN|TEC|OWN)/i.test(empCode)) return true;
    } catch {}
    return false;
  };

  // Resolve admin employee code from any available source. saveChartererData
  // needs a real (non-empty) code for the server-side admin bypass header,
  // otherwise the lock guard rejects with 403 and the UI shows "Error saving".
  const getAdminCode = (): string => {
    if (currentEmployee?.code) return currentEmployee.code;
    try {
      const stored = sessionStorage.getItem('auth_current_user');
      if (stored) {
        const u = JSON.parse(stored);
        if (u?.code) return u.code;
      }
      const empCode = localStorage.getItem('employeeCode');
      if (empCode) return empCode;
    } catch {}
    return '';
  };

  // charterStartDate = booking_data.startDate (ISO 'YYYY-MM-DD'). Build local
  // midnight explicit ώστε να αποφύγουμε το UTC-parse skew του new Date('YYYY-MM-DD').
  const getCharterStartMidnight = (): Date | null => {
    const raw = bookingData?.startDate || bookingData?.checkInDate || '';
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(raw));
    if (!m) return null;
    return new Date(+m[1], +m[2] - 1, +m[3], 0, 0, 0, 0);
  };

  const isWithinEditWindow = () => {
    const start = getCharterStartMidnight();
    if (!start) return false;            // άγνωστη ημ/νία → non-admin locked (safe)
    // Lock 1 hour before actual charter start (startDate + startTime).
    // Falls back to 00:00 if startTime missing.
    const startTime = String(bookingData?.startTime || '00:00');
    const tm = /^(\d{1,2}):(\d{2})/.exec(startTime);
    const deadline = new Date(start);
    if (tm) {
      deadline.setHours(+tm[1], +tm[2], 0, 0);
    }
    deadline.setHours(deadline.getHours() - 1); // 1 hour before start
    return new Date() < deadline;
  };

  const canEdit = () => isAdmin() || isWithinEditWindow();

  // ΚΑΝΟΝΑΣ LOCK (εντολή Kostas): το Submit/lock ΔΕΝ κλειδώνει skipper + crew — αλλάζουν
  // μέχρι την τελευταία στιγμή (skipper αρρωσταίνει, επιβάτες έρχονται/φεύγουν). Επεξεργάσιμα
  // μέχρι το τέλος της ημέρας check-in (charter start day)· ADMIN πάντα. Το ΣΥΜΒΟΛΑΙΟ
  // (στοιχεία ναυλωτή, υπογραφές, όροι) παραμένει στο canEdit() (κλειδώνει ως έχει).
  const canEditCrew = () => {
    if (isAdmin()) return true;
    const start = getCharterStartMidnight();
    if (!start) return true; // άγνωστη ημ/νία → επίτρεψε (real-world flexibility)
    const endOfCheckinDay = new Date(start);
    endOfCheckinDay.setHours(23, 59, 59, 999);
    return new Date() <= endOfCheckinDay;
  };

  // Χωρίς dep array: κάθε render ξαναγράφει τα refs → το poll βλέπει ΠΑΝΤΑ φρέσκα
  // δικαιώματα, χωρίς να χρειάζεται να μπει το bookingData στα deps του loadCrewData.
  useEffect(() => {
    canEditRef.current = canEdit();
    canEditCrewRef.current = canEditCrew();
  });

  // FEATURE: detect logged-in ADMIN/TECHNICAL employee + sessionStorage admin override on mount
  useEffect(() => {
    try {
      const empStr = sessionStorage.getItem('auth_current_user');
      if (empStr) {
        const emp = JSON.parse(empStr);
        if (emp && ['ADMIN', 'TECHNICAL'].includes(emp.role)) {
          setCurrentEmployee(emp);
        }
      }
      if (sessionStorage.getItem('charterAgreementAdminOverride')) {
        setIsAdminMode(true);
      }
    } catch (e) {
      console.warn('Employee/admin detection failed', e);
    }
  }, []);

  // Persist language changes to sessionStorage (shared across all pages)
  useEffect(() => {
    sessionStorage.setItem('yacht_lang', language);
  }, [language]);

  // Canvas drawing helpers
  const getCanvasPos = (canvas: HTMLCanvasElement, e: any) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (canvasRef: React.RefObject<HTMLCanvasElement>, setDrawing: (b: boolean) => void) => (e: any) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getCanvasPos(canvas, e);
    ctx.beginPath();
    ctx.moveTo(pos.x, pos.y);
    setDrawing(true);
  };

  const draw = (canvasRef: React.RefObject<HTMLCanvasElement>, isDrawing: boolean) => (e: any) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pos = getCanvasPos(canvas, e);
    ctx.lineTo(pos.x, pos.y);
    ctx.strokeStyle = '#000';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };

  const stopDrawing = (setDrawing: (b: boolean) => void, canvasRef: React.RefObject<HTMLCanvasElement>, field: 'signature' | 'skipperSignature') => () => {
    setDrawing(false);
    const canvas = canvasRef.current;
    if (!canvas) return;
    // CRITICAL: μη γράφεις state αν ο καμβάς είναι ΑΘΙΚΤΟΣ (όλα pixels α=0).
    // mouseLeave/touchEnd πυροδοτείται και χωρίς πραγματικό stroke — χωρίς αυτόν
    // τον έλεγχο σώζαμε κενό PNG (3314-byte blank canvas) πάνω σε υπάρχουσα υπογραφή.
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    let hasInk = false;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] !== 0) { hasInk = true; break; }
    }
    if (!hasInk) return;
    const dataUrl = canvas.toDataURL('image/png');
    // S4b: το popup «Are you also the skipper?» καταργήθηκε — αντικαταστάθηκε από το dropdown skipper.
    const _now = new Date().toISOString();
    chartererDirtyRef.current = true; // η υπογραφή δεν περνά από onChange — μάρκαρε εδώ
    if (field === 'signature') {
      setChartererData(prev => ({ ...prev, signature: dataUrl, chartererSignedAt: _now }));
    } else {
      setChartererData(prev => ({ ...prev, skipperSignature: dataUrl, skipperSignedAt: _now }));
    }
  };

  const clearCanvas = (canvasRef: React.RefObject<HTMLCanvasElement>, field: 'signature' | 'skipperSignature') => () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    if (field === 'signature') {
      setChartererData(prev => ({ ...prev, signature: '', chartererSignedAt: '' }));
    } else {
      setChartererData(prev => ({ ...prev, skipperSignature: '', skipperSignedAt: '' }));
    }
  };
  const navigate = useNavigate();
  const location = useLocation();

  // Get booking code for use in loadCrewData
  const adminOverride = sessionStorage.getItem('charterAgreementAdminOverride');
  const bookingCode = adminOverride || location.state?.bookingCode || '';

  // 🔥 Auto-refresh: Memoized loadCrewData function
  const loadCrewData = useCallback(async () => {
    if (!bookingCode) return;

    try {
      const savedCrew = await loadCharterCrew(bookingCode);
      if (savedCrew && savedCrew.length > 0 && !(canEditCrewRef.current && crewDirtyRef.current)) {
        console.log('👥 Auto-refresh: Loading crew members:', savedCrew);
        setCrewMembers(dedupeCrew(unifyCrew(savedCrew))); // v6.366 ghost strip + unifyCrew: όνομα/ID→λατινικά, merge ίδιου ID → ΕΝΑ record
        setShowCrewForm(true);
      }

      // Auto-refresh: Load charterer data
      const autoCharterer = await loadChartererData(bookingCode);
      // ΚΑΝΟΝΑΣ (εντολή Kostas): αλλαγές ατόμων μέχρι το check-in. Το poll ΔΕΝ ακυρώνει
      // τον κανόνα: όσο ο χρήστης ΕΠΙΤΡΕΠΕΤΑΙ να γράφει ΚΑΙ έχει αγγίξει τη φόρμα, μένει
      // ανέγγιχτη. Αν δεν επιτρέπεται edit (readOnly), δεν υπάρχει δικό του να χαθεί.
      // Ο έλεγχος ΠΡΕΠΕΙ να τυλίγει ΟΛΟ το if/else — αλλιώς το `else if (bookingData)`
      // παρακάτω θα πατούσε τη φόρμα από το booking αντί για το archive.
      if (canEditRef.current && chartererDirtyRef.current) {
        console.log('⏸️ Auto-refresh: skipping charterer overwrite (unsaved user edits)');
      } else if (autoCharterer && (autoCharterer.firstName || autoCharterer.lastName)) {
        console.log('Auto-refresh: Loading charterer data:', autoCharterer);
        // Pre-fill: merge online data with booking data
        const bk = (bookingData || {}) as any;
        const merged = {
          ...autoCharterer,
          firstName: autoCharterer.firstName || bk.chartererFirstName || bk.skipperFirstName || '',
          lastName: autoCharterer.lastName || bk.chartererLastName || bk.skipperLastName || '',
          address: autoCharterer.address || bk.chartererAddress || bk.skipperAddress || '',
          tel: autoCharterer.tel || bk.chartererPhone || bk.skipperPhone || '',
          email: autoCharterer.email || bk.chartererEmail || bk.skipperEmail || '',
        };
        // {...prev, ...merged}: το merged (archive) υπερισχύει, αλλά πεδία που ΛΕΙΠΟΥΝ από το
        // archive (π.χ. skipper* που φορτώθηκαν στο mount) ΔΕΝ μηδενίζονται από το 5λεπτο poll.
        setChartererData((prev: any) => ({ ...prev, ...merged }));
        setShowChartererForm(true);
      } else if (bookingData) {
        // No online data — pre-fill from booking
        const bk = bookingData as any;
        setChartererData(prev => ({
          ...prev,
          firstName: bk.chartererFirstName || bk.skipperFirstName || '',
          lastName: bk.chartererLastName || bk.skipperLastName || '',
          address: bk.chartererAddress || bk.skipperAddress || '',
          tel: bk.chartererPhone || bk.skipperPhone || '',
          email: bk.chartererEmail || bk.skipperEmail || '',
        }));
      }

      setLastUpdated(new Date());

            // Load already uploaded documents from archive
            try {
              const archiveResp = await fetch('/api/charter-archive.php?booking_number=' + encodeURIComponent(bookingCode));
              if (archiveResp.ok) {
                const archiveResult = await archiveResp.json();
                const archiveData = archiveResult.data || {};
                const docs = archiveData.documents || {};
                if (docs.skipperLicense && docs.skipperLicense.length > 0) {
                  setSkipperLicense(docs.skipperLicense[0].dataUrl || '');
                  console.log('Loaded skipper license from archive');
                }
                if (docs.charterAgreement && docs.charterAgreement.length > 0) {
                  setCharterPartyScan(docs.charterAgreement[0].dataUrl || '');
                  console.log('Loaded charter party scan from archive');
                }
                if (docs.crewList && docs.crewList.length > 0) {
                  setCrewListScan(docs.crewList[0].dataUrl || '');
                  console.log('Loaded crew list scan from archive');
                }
              }
            } catch (archiveErr) {
              console.log('No archive data yet:', archiveErr);
            }

    } catch (error) {
      console.error('❌ Error loading crew data:', error);
    }
  }, [bookingCode]);

  // 🔥 Auto-refresh: Poll crew data every 5 minutes
  const { isRefreshing } = useAutoRefresh(loadCrewData, 5);

  // Το useAutoRefresh στήνει ΜΟΝΟ setInterval — ΔΕΝ καλεί τη fetchFunction στο mount
  // (hooks/useAutoRefresh.ts:17-31). Χωρίς αυτό, το archive (δίπλωμα + σαρώσεις) φορτώνει
  // πρώτη φορά στα 5', και ως τότε η Σύνοψη λέει ψευδώς «Δίπλωμα εκκρεμεί» ενώ υπάρχει.
  // Στενό fix: μία κλήση στο mount. Το ίδιο το hook ΔΕΝ αλλάζει — το χρησιμοποιούν κι άλλες σελίδες.
  useEffect(() => { loadCrewData(); }, [loadCrewData]);

  useEffect(() => {
    const loadBookingFromAPI = async () => {
      console.log('🔍 CharterAgreementPage: Loading booking from API:', bookingCode);

      if (bookingCode) {
        try {
          // Fetch all bookings from API (API is source of truth)
          const allBookings = await getAllBookings();

          // Find matching booking by code (case-insensitive)
          const booking = allBookings.find((b: any) =>
            codeMatches(b.bookingNumber || b.code || b.id, bookingCode)
          );

          console.log('📦 Found booking from API:', booking);

          if (booking) {
            const data = {
              bookingCode: booking.bookingNumber || booking.code || bookingCode,
              ...booking
            };
            console.log('✅ Setting bookingData:', data);
            setBookingData(data);

            // PREFILL_FROM_BOOKING_MARKER
            try {
              const existingOnline = await loadChartererData(bookingCode);
              if (existingOnline && existingOnline.isLocked) {
                console.log('🔒 Loading locked online charterer data');
                setChartererData((prev: any) => ({ ...prev, ...existingOnline }));
                setShowChartererForm(true);
              } else {
                console.log('📋 Pre-filling charterer form from booking data');
                // ΜΙΑ ΠΗΓΗ ΑΛΗΘΕΙΑΣ: φόρτωσε skipper/charterer από ΟΠΟΥ κι αν μπήκαν (π.χ. Fleet) —
                // αλυσίδα fallback: charter_archives.chartererData (existingOnline) → booking_data.chartererData
                // (nested cd) → flat booking_data.* → γραμμή crewMembers[role='skipper'] (skRow).
                // Το Fleet handleSaveSkipperRow γράφει σε archive + booking chartererData.skipper* — τώρα
                // το βλέπει το Agreement. Ο καθρέφτης v6.464 μεταφέρει μετά τα skipper* στο crewMembers.
                const cd: any = data.chartererData || {};
                const skRow: any = (data.crewMembers || []).find((m: any) => String(m?.role || '').toLowerCase() === 'skipper') || {};
                const pick = (...vals: any[]) => { for (const v of vals) { if (String(v ?? '').trim() !== '') return v; } return ''; };
                const prefill: any = {
                  firstName: pick(existingOnline?.firstName, cd.firstName, data.chartererFirstName),
                  lastName: pick(existingOnline?.lastName, cd.lastName, data.chartererLastName),
                  address: pick(existingOnline?.address, cd.address, data.chartererAddress),
                  tel: pick(existingOnline?.tel, cd.tel, cd.phone, data.chartererPhone),
                  email: pick(existingOnline?.email, cd.email, data.chartererEmail),
                  idNumber: pick(existingOnline?.idNumber, cd.idNumber, cd.passport, data.chartererIdNumber, data.chartererPassport),
                  gender: pick(existingOnline?.gender, cd.gender, data.chartererGender),
                  dob: pick(existingOnline?.dob, cd.dob, cd.dateOfBirth, data.chartererDob),
                  nationality: pick(existingOnline?.nationality, cd.nationality, data.chartererNationality),
                  taxNumber: pick(existingOnline?.taxNumber, cd.taxNumber, data.chartererTaxNumber),
                  taxOffice: pick(existingOnline?.taxOffice, cd.taxOffice, data.chartererTaxOffice),
                  idType: pick(existingOnline?.idType, cd.idType, data.chartererIdType) || 'id',
                  skipperIdType: pick(existingOnline?.skipperIdType, cd.skipperIdType, data.skipperIdType) || 'id',
                  signature: pick(existingOnline?.signature, cd.signature),
                  isAlsoSkipper: existingOnline?.isAlsoSkipper ?? cd.isAlsoSkipper ?? false,
                  skipperMode: pick(existingOnline?.skipperMode, cd.skipperMode, data.skipperMode),
                  // SKIPPER detail — fallback: archive → booking nested → flat → crewMembers[skipper]
                  skipperFirstName: pick(existingOnline?.skipperFirstName, cd.skipperFirstName, data.skipperFirstName, skRow.firstName),
                  skipperLastName: pick(existingOnline?.skipperLastName, cd.skipperLastName, data.skipperLastName, skRow.lastName),
                  skipperName: pick(existingOnline?.skipperName, cd.skipperName, data.skipperName, skRow.name),
                  skipperIdNumber: pick(existingOnline?.skipperIdNumber, existingOnline?.skipperPassport, cd.skipperIdNumber, cd.skipperPassport, data.skipperPassport, data.skipperIdNumber, skRow.passport),
                  skipperDob: pick(existingOnline?.skipperDob, cd.skipperDob, data.skipperDob, skRow.dateOfBirth),
                  skipperGen: pick(existingOnline?.skipperGen, cd.skipperGen, data.skipperGender, skRow.gender),
                  skipperNationality: pick(existingOnline?.skipperNationality, cd.skipperNationality, data.skipperNationality, skRow.nationality),
                  skipperAddress: pick(existingOnline?.skipperAddress, cd.skipperAddress, data.skipperAddress),
                  skipperPhone: pick(existingOnline?.skipperPhone, cd.skipperPhone, data.skipperPhone, skRow.phone),
                  skipperEmail: pick(existingOnline?.skipperEmail, cd.skipperEmail, data.skipperEmail, skRow.email),
                  skipperSignature: pick(existingOnline?.skipperSignature),
                  skipperTaxNumber: pick(existingOnline?.skipperTaxNumber, cd.skipperTaxNumber, data.skipperTaxNumber),
                  skipperTaxOffice: pick(existingOnline?.skipperTaxOffice, cd.skipperTaxOffice, data.skipperTaxOffice),
                  isLocked: false
                };
                setChartererData((prev: any) => ({ ...prev, ...prefill }));
              }
            } catch (prefillErr) {
              console.log('Pre-fill error (non-fatal):', prefillErr);
            }

            // Load crew members from API data
            const savedCrew = data.crewMembers || booking.charterAgreementData?.crewMembers;
            if (savedCrew && savedCrew.length > 0) {
              console.log('👥 Loading saved crew members:', savedCrew);
              setCrewMembers(dedupeCrew(unifyCrew(savedCrew))); // v6.366 ghost strip + unifyCrew: όνομα/ID→λατινικά, merge ίδιου ID → ΕΝΑ record
              setShowCrewForm(true);
            }
            setLastUpdated(new Date());
          } else {
            console.warn('⚠️ No booking found in API for:', bookingCode);
          }
        } catch (error) {
          console.error('❌ Error loading booking from API:', error);
        }
      } else {
        console.warn('⚠️ No booking code found!');
      }
    };

    loadBookingFromAPI();
  }, [bookingCode]);

  // 🔥 MOBILE COLLAPSIBLE SECTIONS — only on ≤767px
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    if (!mq.matches) return;
    const cleanup: Array<() => void> = [];
    const setup = () => {
      const sections = document.querySelectorAll('.ca-section');
      sections.forEach((sec: any) => {
        if (sec.dataset.caInit === '1') return;
        sec.dataset.caInit = '1';
        const h2 = sec.querySelector('h2');
        if (!h2) return;
        // Style h2 as clickable header
        h2.style.cursor = 'pointer';
        h2.style.minHeight = '44px';
        h2.style.display = 'flex';
        h2.style.alignItems = 'center';
        h2.style.justifyContent = 'space-between';
        h2.style.userSelect = 'none';
        // Add chevron
        if (!h2.querySelector('.ca-chevron')) {
          const chev = document.createElement('span');
          chev.className = 'ca-chevron';
          chev.textContent = '▶';
          chev.style.cssText = 'margin-left:auto;transition:transform 0.3s;font-size:16px;color:#6b7280;flex-shrink:0;';
          h2.appendChild(chev);
        }
        const handler = () => {
          sec.classList.toggle('ca-open');
          const chev: any = h2.querySelector('.ca-chevron');
          if (chev) chev.style.transform = sec.classList.contains('ca-open') ? 'rotate(90deg)' : 'rotate(0deg)';
        };
        h2.addEventListener('click', handler);
        cleanup.push(() => h2.removeEventListener('click', handler));
      });
    };
    // Setup after small delay to let DOM settle
    const t1 = setTimeout(setup, 100);
    const t2 = setTimeout(setup, 500);
    const t3 = setTimeout(setup, 1500);
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      cleanup.forEach(fn => fn());
    };
  }, [bookingCode, chartererData, invitations]);

  // Phase 6: Crew invitations handlers
  const loadInvitations = async (bkCode: string) => {
    if (!bkCode) return;
    try {
      const r = await fetch('/api/crew-invitations.php?action=list&booking_number=' + encodeURIComponent(bkCode));
      const j = await r.json();
      if (j.success && Array.isArray(j.data)) {
        setInvitations(j.data);
        const submitted = j.data.filter((i: any) => i.status === 'submitted');
        if (submitted.length > 0) {
          setCrewMembers((prev: any[]) => {
            const byKey = new Map();
            // v6.469: ΚΡΑΤΑ και τα μέλη ΧΩΡΙΣ passport (πριν έπεφταν σιωπηλά από το Map) —
            // αλλιώς μια νεοφτιαγμένη γραμμή skipper χωρίς ID χανόταν μόλις φόρτωναν invitations.
            prev.forEach((m: any, mi: number) => byKey.set(m?.passport || ('__row' + mi), m));
            submitted.forEach((i: any) => {
              const fullName = ((i.submitted_first_name || '') + ' ' + (i.submitted_last_name || '')).trim();
              if (i.role === 'skipper') {
                // Skipper invitation: γέμισε τα chartererData.skipper* ΚΑΙ (v6.469) συνέχισε παρακάτω
                // ώστε να μπει ΚΑΙ γραμμή crewMembers[role='skipper'] — πριν έκανε return και ο σκίπερ
                // δεν φαινόταν ποτέ στο Crew List. Εξαίρεση: isAlsoSkipper ('me') → ο σκίπερ = ναυλωτής.
                setChartererData((cd: any) => {
                  if (cd && cd.isAlsoSkipper) return cd;
                  return Object.assign({}, cd, {
                    skipperName: fullName,
                    skipperFirstName: i.submitted_first_name || '',
                    skipperLastName: i.submitted_last_name || '',
                    skipperPassport: i.submitted_passport || '',
                    skipperPhone: i.submitted_phone || '',
                    skipperEmail: i.submitted_email || i.invite_email || '',
                    skipperAddress: i.submitted_address || '',
                    skipperNationality: i.submitted_nationality || '',
                    skipperDob: i.submitted_date_of_birth || '',
                    skipperSignature: i.signature_data || '',
                    skipperFromInvitation: true,
                  });
                });
                if (chartererData && (chartererData as any).isAlsoSkipper) return; // 'me' → καμία ξεχωριστή γραμμή
              }
              const passport = i.submitted_passport || '';
              const entry = {
                name: fullName,
                firstName: i.submitted_first_name || '',
                lastName: i.submitted_last_name || '',
                passport: passport,
                dateOfBirth: i.submitted_date_of_birth || '',
                nationality: i.submitted_nationality || '',
                email: i.submitted_email || i.invite_email || '',
                phone: i.submitted_phone || '',
                address: i.submitted_address || '',
                role: i.role || 'crew',
                signature: i.signature_data || '',
                fromInvitation: true,
                invitationToken: i.token,
              };
              {
                  const k = passport || ('__' + i.token);
                  const ex = byKey.get(k);
                  if (ex) {
                    // merge: κράτα τις μη-κενές (edited) τιμές του booking, το invitation γεμίζει μόνο κενά
                    const merged: any = Object.assign({}, entry);
                    Object.keys(entry).forEach((kk) => {
                      const exv = (ex as any)[kk];
                      if (exv !== undefined && exv !== null && exv !== '') merged[kk] = exv;
                    });
                    merged.fromInvitation = true;
                    merged.invitationToken = entry.invitationToken;
                    byKey.set(k, merged);
                  } else {
                    byKey.set(k, entry);
                  }
                }
            });
            return Array.from(byKey.values());
          });
          if (!showCrewForm) setShowCrewForm(true);
        }
      }
    } catch (e) { console.error('loadInvitations failed', e); }
  };

  const sendInvitation = async () => {
    if (!bookingCode) { setInviteMsg(t.ca_noBookingCodeMsg); return; }
    if (!inviteForm.email) { setInviteMsg(t.ca_emailRequired); return; }
    setInviteBusy(true); setInviteMsg('');
    try {
      const r = await fetch('/api/crew-invitations.php?action=create', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          booking_number: bookingCode,
          role: inviteForm.role,
          first_name: inviteForm.firstName,
          last_name: inviteForm.lastName,
          email: inviteForm.email,
          phone: inviteForm.phone,
          language: language,
        })
      });
      const j = await r.json();
      if (j.success) {
        await fetch('/api/crew-invitations.php?action=resend', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: j.data.token })
        });
        setInviteMsg(t.ca_invitationSentTo + ' ' + inviteForm.email);
        setInviteForm({ firstName: '', lastName: '', email: '', phone: '+30 ', role: 'crew' });
        await loadInvitations(bookingCode);
      } else {
        setInviteMsg('Error: ' + (j.error || 'Failed'));
      }
    } catch (e) {
      setInviteMsg('Network error');
    } finally {
      setInviteBusy(false);
    }
  };

  const resendInvitation = async (tk: string) => {
    setInviteBusy(true); setInviteMsg('');
    try {
      const r = await fetch('/api/crew-invitations.php?action=resend', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tk })
      });
      const j = await r.json();
      setInviteMsg(j.success ? t.ca_invitationResent : t.ca_resendFailed);
      if (j.success && bookingCode) await loadInvitations(bookingCode);
    } finally { setInviteBusy(false); }
  };

  const deleteInvitation = async (tk: string) => {
    if (!window.confirm(t.ca_deleteConfirm)) return;
    setInviteBusy(true);
    try {
      await fetch('/api/crew-invitations.php?action=delete', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: tk })
      });
      if (bookingCode) await loadInvitations(bookingCode);
    } finally { setInviteBusy(false); }
  };

  // WhatsApp: ανοίγει wa.me με έτοιμο μήνυμα + invite link. Χωρίς αριθμό → ο χρήστης διαλέγει επαφή.
  const waInvite = (inv: any) => {
    const link = window.location.origin + '/crew-invite/' + inv.token;
    const w = WA_MSG[language] || WA_MSG.en;
    const name = (inv.invite_first_name || '').trim();
    const greeting = name ? (w.hello + ' ' + name) : w.hello;
    const msg = encodeURIComponent(greeting + ', ' + w.body + ' ' + link);
    const num = waNumber(inv.invite_phone || '');
    window.open(num ? ('https://wa.me/' + num + '?text=' + msg) : ('https://wa.me/?text=' + msg), '_blank');
  };

  useEffect(() => {
    if (bookingCode) loadInvitations(bookingCode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingCode, bookingData]);




  // === Handlers for CharterAgreementPage ===
  const handleDownloadCharterParty = async () => {
    if (!bookingData) {
      alert(t.ca_alertNoBookingExcl);
      return;
    }
    // Charter Party requires online form filled + signed; show modal instead of downloading if missing
    if (!chartererData?.signature || !chartererData?.isLocked) {
      setShowCharterPartyModal(true);
      return;
    }
    const charter = bookingData;
    const vesselTypes: any = {'Bob':'Catamaran','Perla':'Catamaran','Infinity':'Catamaran','Maria 1':'Monohull','Maria 2':'Monohull','Bar Bar':'Monohull','Kalispera':'Monohull','Valesia':'Monohull'};
    let boatType = vesselTypes[bookingData.vesselName] || '';
    try {
      const vRes = await fetch('https://yachtmanagementsuite.com/api/vessel-owners.php');
      if (vRes.ok) {
        const vessels = await vRes.json();
        const found = vessels.find((v: any) => v.vessel_name === bookingData.vesselName);
        if (found) boatType = found.vessel_type || '';
      }
    } catch(e) { console.log('Could not fetch vessels:', e); }
    const boat = { name: bookingData.vesselName, id: bookingData.vesselId, type: boatType };
    generateCharterParty(charter, boat);
  };

  const handleDownloadCrewList = () => {
    if (!bookingData) {
      alert(t.ca_alertNoBookingExcl);
      return;
    }
    // Crew List always downloadable — customer can fill blank template by hand
    const charter = bookingData;
    const vTypes: any = {'Bob':'Catamaran','Perla':'Catamaran','Infinity':'Catamaran','Maria 1':'Monohull','Maria 2':'Monohull','Bar Bar':'Monohull','Kalispera':'Monohull','Valesia':'Monohull'};
    const boat = { name: bookingData.vesselName, id: bookingData.vesselId, type: vTypes[bookingData.vesselName] || '' };
    const boatDetails = {};
    generateCrewList(charter, boat, boatDetails);
  };

  const handleSkipperLicenseUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const dataUrl = event.target?.result as string;
        if (!skipperLicense) { setSkipperLicense(dataUrl); } else { setExtraLicenses(prev => [...prev, dataUrl]); }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setSkipperLicense(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // Render a license file: PDFs (or non-image data URLs) get a 📄 View/Download UI
  // instead of a broken <img>. Images render as before.
  const isPdfLicense = (s: string) => typeof s === 'string' && (s.startsWith('data:application/pdf') || s.startsWith('data:application/octet-stream'));
  const renderLicensePreview = (src: string, label: string) => (
    isPdfLicense(src) ? (
      <div className="flex flex-col items-center gap-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <div className="text-5xl">📄</div>
        <p className="text-sm font-semibold text-gray-700">{label} (PDF)</p>
        <div className="flex gap-2 justify-center">
          <a href={src} target="_blank" rel="noopener noreferrer" className="px-4 py-2 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 no-underline">👁 View</a>
          <a href={src} download={label.replace(/\s+/g, '_') + '.pdf'} className="px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 no-underline">⬇ Download</a>
        </div>
      </div>
    ) : (
      <img src={src} alt={label} className="max-w-full max-h-64 mx-auto rounded-lg shadow-md" />
    )
  );

  const handleCharterPartyScanUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCharterPartyScan(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrewListScanUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setCrewListScan(event.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  // S4b: εφαρμόζει την επιλογή του dropdown skipper (κρατά το isAlsoSkipper σε συγχρονισμό).
  const applySkipperMode = (mode: any) => {
    setSkipperMode(mode);
    setChartererData((prev: any) => {
      if (mode === 'me') {
        return { ...prev, skipperMode: mode, isAlsoSkipper: true,
          skipperFirstName: prev.firstName || '', skipperLastName: prev.lastName || '',
          skipperName: `${prev.firstName || ''} ${prev.lastName || ''}`.trim(),
          skipperAddress: prev.address || '', skipperPhone: prev.tel || '', skipperEmail: prev.email || '',
          skipperIdType: prev.idType || 'id', skipperIdNumber: prev.idNumber || '',
          skipperTaxNumber: prev.taxNumber || '', skipperTaxOffice: prev.taxOffice || '',
          skipperDob: prev.dob || '', skipperNationality: prev.nationality || '', skipperGen: prev.gender || '' };
      }
      // Το wipe ΔΕΝ τρέχει όταν υπάρχει ήδη skipper στο crew list: τα στοιχεία του είναι
      // authoritative (μπήκαν από online crew list / invitation) και ο καθρέφτης
      // crew list → φόρμα θα τα ξαναγέμιζε αμέσως. Αλλάζει μόνο mode/isAlsoSkipper.
      // ΑΦΜ/ΔΟΥ διατηρούνται κι αυτά — δεν υπάρχουν στο crew schema, μόνο εδώ ζουν.
      const hasCrewSkipper = (crewMembers || []).some((m: any) => String(m?.role || '').toLowerCase() === 'skipper');
      if (hasCrewSkipper) return { ...prev, skipperMode: mode, isAlsoSkipper: false };
      return { ...prev, skipperMode: mode, isAlsoSkipper: false, skipperFirstName: '', skipperLastName: '', skipperName: '',
        skipperAddress: '', skipperPhone: '', skipperEmail: '', skipperIdType: 'passport', skipperIdNumber: '',
        skipperTaxNumber: '', skipperTaxOffice: '', skipperDob: '', skipperNationality: '', skipperGen: '' };
    });
  };
  // S4b: το skipperMode διαβάζεται ΡΗΤΑ από το αποθηκευμένο (nested chartererData ή flat
  // booking_data) — ΚΑΜΙΑ inference από γεμάτα πεδία. Legacy fallback μόνο αν δεν υπάρχει.
  useEffect(() => {
    if (skipperModeInit.current) return;
    // ΠΕΡΙΜΕΝΕ το bookingData: εκεί ζουν τα flat skipperMode/sameAsSkipper. Αλλιώς το effect
    // τρέχει με μόνο το chartererData prefill (isAlsoSkipper=false) → 'other' και κλειδώνει λάθος.
    if (!bookingData) return;
    skipperModeInit.current = true;
    const cd: any = chartererData || {};
    const stored = cd.skipperMode || (bookingData as any)?.skipperMode;
    if (stored && ['me', 'crew', 'other', 'invite'].includes(stored)) {
      setSkipperMode(stored);
      if (stored === 'me' && !cd.isAlsoSkipper) setChartererData((p: any) => ({ ...p, isAlsoSkipper: true }));
      return;
    }
    // Legacy (bookings πριν την ρητή αποθήκευση) → infer μία φορά.
    if (cd.isAlsoSkipper || (bookingData as any)?.sameAsSkipper) {
      setSkipperMode('me');
      if (!cd.isAlsoSkipper) setChartererData((p: any) => ({ ...p, isAlsoSkipper: true }));
    } else if ((crewMembers || []).some((m: any) => m.role === 'skipper')) {
      setSkipperMode('crew');
    } else { setSkipperMode('other'); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartererData, crewMembers, bookingData]);

  // BUG C: skipperMode='me' → τα προσωπικά πεδία skipper (Gender/DOB/Nationality) κληρονομούνται
  // LIVE από τον charterer (μία φορά όλα). Τα πεδία είναι κρυμμένα· το validation δεν τα ζητά.
  useEffect(() => {
    if (skipperMode !== 'me') return;
    setChartererData((prev: any) => {
      if (prev.skipperGen === (prev.gender || '') && prev.skipperDob === (prev.dob || '') && prev.skipperNationality === (prev.nationality || '')) return prev;
      return { ...prev, skipperGen: prev.gender || '', skipperDob: prev.dob || '', skipperNationality: prev.nationality || '' };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skipperMode, chartererData.gender, chartererData.dob, chartererData.nationality]);

  // ΠΑΛΙΑ ΝΑΥΛΑ: γέμισε ΚΕΝΑ πεδία charterer από το crew record του (isCharterer/name/email match).
  // Μόνο EMPTY fields — δεν πατάει ό,τι έχει ήδη τιμή ή ό,τι πληκτρολογεί ο χρήστης. Καμία migration.
  useEffect(() => {
    const rec = findChartererCrewRecord(crewMembers, chartererData);
    if (!rec) return;
    setChartererData((prev: any) => {
      const cand: any = {};
      const fill = (k: string, v: any) => { if (!String(prev[k] ?? '').trim() && String(v ?? '').trim()) cand[k] = v; };
      const parts = String(rec.name || '').replace(/\s+/g, ' ').trim().split(' ');
      const fn = rec.firstName || parts[0] || '';
      const ln = rec.lastName || (parts.length > 1 ? parts.slice(1).join(' ') : '');
      fill('firstName', fn);
      fill('lastName', ln);
      fill('idNumber', rec.passport);
      fill('dob', rec.dateOfBirth);
      fill('gender', rec.gender);
      fill('nationality', rec.nationality);
      fill('tel', rec.phone);
      fill('email', rec.email);
      fill('address', rec.address);
      return Object.keys(cand).length ? { ...prev, ...cand } : prev;
    });
    // Deps: crewMembers + key charterer πεδία → self-healing αν μεταγενέστερο load τα αδειάσει.
    // Το empty-only guard (fill μόνο κενών) εγγυάται σύγκλιση — κανένα infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewMembers, chartererData.firstName, chartererData.idNumber, chartererData.dob, chartererData.nationality]);

  // v6.473 — ΚΑΝΟΝΑΣ «ΟΤΑΝ Ο ΣΚΙΠΕΡ ΕΙΝΑΙ ΑΛΛΟ ΠΡΟΣΩΠΟ»: τα πεδία σκίπερ μένουν ΚΕΝΑ μέχρι να
  // δοθούν τα ΠΡΑΓΜΑΤΙΚΑ του στοιχεία. Το app ΔΕΝ βάζει ΠΟΤΕ μόνο του στοιχεία ναυλωτή εκεί
  // (ούτε email/τηλέφωνο/ημ.γέννησης/φύλο/εθνικότητα/ID). Αντιγραφή ΜΟΝΟ με ρητό mode='me'.
  // Επιστρέφει true αν η γραμμή crew είναι στην πραγματικότητα ο ίδιος ο ναυλωτής.
  const sameAsChartererRow = (rec: any) => {
    if (!rec) return false;
    if (rec.isCharterer === true) return true;
    const nrm = (x: any) => String(x == null ? '' : x).toLowerCase().replace(/[^a-z0-9]/gi, '');
    const cEmail = String(chartererData.email || '').trim().toLowerCase();
    const rEmail = String(rec.email || '').trim().toLowerCase();
    if (cEmail && rEmail && cEmail === rEmail) return true;
    const cId = nrm(chartererData.idNumber || (chartererData as any).passport);
    const rId = nrm(rec.passport);
    if (cId && rId && cId === rId) return true;
    const cName = nrm(`${chartererData.firstName || ''}${chartererData.lastName || ''}`);
    const rName = nrm(`${rec.firstName || ''}${rec.lastName || ''}`) || nrm(rec.name);
    if (cName && rName && cName === rName) return true;
    return false;
  };

  // ΚΑΘΡΕΦΤΗΣ ΤΟΥ ΠΑΡΑΠΑΝΩ, ΓΙΑ ΤΟΝ SKIPPER: γέμισε ΚΕΝΑ πεδία skipper* από τη γραμμή
  // crewMembers[role='skipper'] (μπήκε από online crew list ή invitation). ΙΔΙΑ αντιστοίχιση,
  // ίδιο empty-only guard — δεν πατάει ό,τι έχει ήδη τιμή ή ό,τι πληκτρολογεί ο χρήστης.
  // ΑΦΜ/ΔΟΥ (skipperTaxNumber/skipperTaxOffice) ΔΕΝ υπάρχουν στο crew schema: μένουν
  // αποκλειστικά στη φόρμα — γι' αυτό δεν τα αγγίζει καθόλου. READ-ONLY: καμία εγγραφή
  // στα crewMembers, καμία migration.
  useEffect(() => {
    if (skipperMode === 'me') return; // 'me' → κληρονομεί από τον charterer (βλ. effect παραπάνω)
    const rec: any = (crewMembers || []).find((m: any) => String(m?.role || '').toLowerCase() === 'skipper');
    if (!rec) return;
    if (sameAsChartererRow(rec)) return; // v6.473: ο ναυλωτής ΔΕΝ τροφοδοτεί τα πεδία σκίπερ
    setChartererData((prev: any) => {
      const cand: any = {};
      const fill = (k: string, v: any) => { if (!String(prev[k] ?? '').trim() && String(v ?? '').trim()) cand[k] = v; };
      const parts = String(rec.name || '').replace(/\s+/g, ' ').trim().split(' ');
      const fn = rec.firstName || parts[0] || '';
      const ln = rec.lastName || (parts.length > 1 ? parts.slice(1).join(' ') : '');
      fill('skipperFirstName', fn);
      fill('skipperLastName', ln);
      fill('skipperName', String(rec.name || '').replace(/\s+/g, ' ').trim());
      fill('skipperIdNumber', rec.passport);
      fill('skipperDob', rec.dateOfBirth);
      fill('skipperGen', rec.gender);
      fill('skipperNationality', rec.nationality);
      fill('skipperPhone', rec.phone);
      fill('skipperEmail', rec.email);
      fill('skipperAddress', rec.address);
      return Object.keys(cand).length ? { ...prev, ...cand } : prev;
    });
    // Deps: ίδιο μοτίβο με τον καθρέφτη του charterer — empty-only guard εγγυάται σύγκλιση.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewMembers, skipperMode, chartererData.skipperFirstName, chartererData.skipperIdNumber, chartererData.skipperDob, chartererData.skipperNationality]);

  // ΑΝΤΙΣΤΡΟΦΟΣ ΚΑΘΡΕΦΤΗΣ: φόρμα σκίπερ (chartererData.skipper*) → γραμμή crewMembers[role='skipper'].
  // Το crewMembers[skipper] είναι SINGLE SOURCE OF TRUTH (Σύνοψη + validation :~1351 + DOCX), αλλά η
  // φόρμα (:~2047-2078) γράφει ΜΟΝΟ flat chartererData.skipper*. Χωρίς αυτό ό,τι πληκτρολογεί ο χρήστης
  // δεν φτάνει ποτέ στο crew row → validation μπλοκάρει. Mismatch ονομάτων: skipperIdNumber→passport,
  // skipperGen→gender, skipperDob→dateOfBirth, skipperNationality→nationality.
  // EMPTY-ONLY (ίδιο guard με τον forward καθρέφτη): γράφει crew field ΜΟΝΟ αν είναι κενό & το flat non-empty.
  // Και οι δύο γέφυρες empty-only → συγκλίνουν, ΚΑΝΕΝΑ loop (δεν μηδενίζει ο ένας τον άλλο).
  useEffect(() => {
    if (skipperMode === 'me') return; // 'me' → ο σκίπερ ΕΙΝΑΙ ο charterer, όχι crew row
    if (skipperMode === 'invite') return; // 'invite' → δεν έχει συμπληρωθεί ακόμη
    const idx = (crewMembers || []).findIndex((m: any) => String(m?.role || '').toLowerCase() === 'skipper');
    // AUTO-SYNC (v6.469): δεν υπάρχει γραμμή skipper αλλά ΥΠΑΡΧΟΥΝ στοιχεία σκίπερ (Fleet /
    // invitation / legacy booking) → ΦΤΙΑΞΕ την. Πριν: early-return → ο σκίπερ έμενε μόνο στα
    // flat/nested και δεν φαινόταν ποτέ στο Crew List (π.χ. ναύλος 726).
    if (idx < 0) {
      if (!skipperModeInit.current) return; // περίμενε το πραγματικό mode· αλλιώς default 'other' → λάθος γραμμή
      const fn = String(chartererData.skipperFirstName || '').trim();
      const ln = String(chartererData.skipperLastName || '').trim();
      const id = String(chartererData.skipperIdNumber || '').trim();
      // v6.473: ΟΝΟΜΑ υποχρεωτικό. Πριν αρκούσε ΜΟΝΟ το ID → φτιαχνόταν ανώνυμη γραμμή
      // σκίπερ από leftover flat (ναύλος 780). Χωρίς όνομα δεν υπάρχει σκίπερ.
      if (!fn && !ln) return;
      // ΚΑΝΟΝΑΣ «άλλο πρόσωπο»: ποτέ γραμμή σκίπερ που είναι ο ίδιος ο ναυλωτής.
      if (sameAsChartererRow({ firstName: fn, lastName: ln, name: `${fn} ${ln}`.trim(), passport: id, email: chartererData.skipperEmail })) return;
      setCrewMembers((prev: any[]) => {
        const list = Array.isArray(prev) ? prev : [];
        if (list.some((m: any) => String(m?.role || '').toLowerCase() === 'skipper')) return prev; // guard διπλής γραμμής
        return [...list, {
          role: 'skipper',
          name: toLatin(chartererData.skipperName || `${fn} ${ln}`.trim()),
          firstName: toLatin(fn),
          lastName: toLatin(ln),
          passport: latinId(id),
          dateOfBirth: chartererData.skipperDob || '',
          gender: chartererData.skipperGen || '',
          nationality: chartererData.skipperNationality || '',
          email: chartererData.skipperEmail || '',
          phone: chartererData.skipperPhone || '',
          address: chartererData.skipperAddress || '',
          taxNumber: chartererData.skipperTaxNumber || '',
          taxOffice: chartererData.skipperTaxOffice || '',
        }];
      });
      return;
    }
    const rec: any = crewMembers[idx];
    const patch: any = {};
    const fill = (crewKey: string, flatVal: any) => {
      if (!String(rec[crewKey] ?? '').trim() && String(flatVal ?? '').trim()) patch[crewKey] = flatVal;
    };
    fill('passport', latinId(chartererData.skipperIdNumber)); // ID→ομόγλυφα (ΑΡ→AP)
    fill('gender', chartererData.skipperGen);
    fill('dateOfBirth', chartererData.skipperDob);
    fill('nationality', chartererData.skipperNationality);
    // Επέκταση: τα υπόλοιπα στοιχεία επικοινωνίας/ταυτότητας από το Fleet flat → crew (ΕΝΑ record).
    fill('email', chartererData.skipperEmail);
    fill('phone', chartererData.skipperPhone);
    fill('address', chartererData.skipperAddress);
    fill('taxNumber', chartererData.skipperTaxNumber);
    fill('taxOffice', chartererData.skipperTaxOffice);
    fill('firstName', toLatin(chartererData.skipperFirstName));
    fill('lastName', toLatin(chartererData.skipperLastName));
    fill('name', toLatin(chartererData.skipperName || `${chartererData.skipperFirstName || ''} ${chartererData.skipperLastName || ''}`.trim()));
    if (Object.keys(patch).length) {
      setCrewMembers((prev: any[]) => prev.map((m: any, i: number) => i === idx ? { ...m, ...patch } : m));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewMembers, skipperMode, chartererData.skipperIdNumber, chartererData.skipperGen, chartererData.skipperDob, chartererData.skipperNationality, chartererData.skipperEmail, chartererData.skipperPhone, chartererData.skipperAddress, chartererData.skipperTaxNumber, chartererData.skipperTaxOffice, chartererData.skipperFirstName, chartererData.skipperLastName, chartererData.skipperName]);

  // ── WRITE-THROUGH (ρητή επεξεργασία χρήστη = αυθεντία) ──────────────────────────────
  // Οι onChange handlers των φορμών καλούν editSkipper/editCharterer → γράφουν chartererData
  // ΚΑΙ ΠΑΝΤΑ overwrite τη γραμμή crewMembers (νικάει ακόμη κι αν υπάρχει ήδη τιμή).
  // NO-LOOP: αυτά καλούνται ΜΟΝΟ από onChange (user events). Το load/prefill καλεί setChartererData
  // ΑΠΕΥΘΕΙΑΣ (όχι editSkipper) → κανένα write-through στη φόρτωση. Ο empty-only καθρέφτης (useEffect)
  // μένει για τη ΦΟΡΤΩΣΗ (γεμίζει μόνο κενά). Μετά από write-through το crew είναι γεμάτο → ο empty-only
  // το προσπερνά → δεν αναιρείται, δεν κάνει loop.
  const isSkipperRow = (m: any) => String(m?.role || '').trim().toLowerCase() === 'skipper';
  const isSelfRow = (m: any) => m?.isCharterer === true;
  // match by role (trim+case-insensitive) → fallback by ID (normId) → αλλιώς console.warn (ΟΧΙ σιωπηλό).
  // idHint = το ΤΡΕΧΟΝ (παλιό, από closure) ID του ατόμου, που ταιριάζει το passport της υπάρχουσας γραμμής
  // ακόμη κι όταν αλλάζει το ίδιο το ID field.
  // createAs (v6.469): αν δεν ταιριάξει καμία γραμμή, ΦΤΙΑΞΕ νέα με αυτά τα πεδία (π.χ. {role:'skipper'})
  // αντί για console.warn+no-op. Όταν λείπει (charterer) η συμπεριφορά μένει ΑΚΡΙΒΩΣ όπως πριν.
  const writeThroughCrew = (match: (m: any) => boolean, crewPatch: any, idHint?: any, createAs?: any) => {
    if (!crewPatch || !Object.keys(crewPatch).length) return;
    setCrewMembers((prev: any[]) => {
      const list = Array.isArray(prev) ? prev : [];
      let idx = list.findIndex(match);
      if (idx < 0 && String(idHint ?? '').trim()) {
        const key = normId(idHint);
        if (key) idx = list.findIndex((m: any) => { const k = normId(m?.passport); return !!k && k === key; });
      }
      if (idx < 0) {
        if (!createAs) {
          console.warn('[write-through] καμία γραμμή crew δεν ταίριαξε', { crewPatch, idHint, roles: list.map((m: any) => m?.role) });
          return prev;
        }
        return [...list, { name: '', passport: '', dateOfBirth: '', nationality: '', gender: '', email: '', phone: '', address: '', ...createAs, ...crewPatch }];
      }
      return list.map((m: any, i: number) => (i === idx ? { ...m, ...crewPatch } : m));
    });
  };
  // Επιτρέπεται γραμμή skipper; 'me' → ο σκίπερ ΕΙΝΑΙ ο charterer· 'invite' → δεν ήρθε ακόμη.
  const skipperRowAllowed = () => skipperMode !== 'me' && skipperMode !== 'invite';
  const editSkipper = (chartererPatch: any, crewPatch: any) => {
    // v6.473: ο χρήστης άδειασε πεδίο σκίπερ → ΡΗΤΗ πρόθεση διαγραφής (ελέγχεται στο submit
    // μαζί με «όλα τα skipper* κενά»). Μόνο από onChange — ποτέ από load/prefill.
    if (chartererPatch && Object.values(chartererPatch).some((v: any) => String(v ?? '').trim() === '')) {
      skipperUserClearedRef.current = true;
    }
    setChartererData((prev: any) => ({ ...prev, ...chartererPatch }));
    writeThroughCrew(isSkipperRow, crewPatch, chartererData.skipperIdNumber, skipperRowAllowed() ? { role: 'skipper' } : null);
  };
  const editCharterer = (chartererPatch: any, crewPatch: any) => {
    setChartererData((prev: any) => ({ ...prev, ...chartererPatch }));
    writeThroughCrew(isSelfRow, crewPatch, chartererData.idNumber);
  };

  /* ═══ ΦΥΣΙΚΟ ΠΡΟΣΩΠΟ Ή ΕΤΑΙΡΕΙΑ (19/09/2026) ═══════════════════════════════
     Νομικό πρόσωπο: ούτε φύλο, ούτε γέννηση, ούτε εθνικότητα, ούτε διαβατήριο —
     και ΔΕΝ επιβαίνει (δεν μπαίνει γραμμή στο crew list). Ένας κανόνας για
     φόρμα, ναυλοσύμφωνο και ΑΑΔΕ: utils/chartererType. */
  const chartererIsCompany = isCompanyCharterer(chartererData);
  const setChartererKind = (company: boolean) => {
    setChartererData((prev: any) => {
      if (!company) return { ...prev, isCompany: false };
      const name = String(prev.companyName || `${prev.firstName || ''} ${prev.lastName || ''}`).trim();
      const next: any = { ...prev, ...companyChartererPatch(name, prev.country), isAlsoSkipper: false };
      INDIVIDUAL_KEYS.forEach((k) => { delete next[k]; });
      return next;
    });
    if (company) setCrewMembers((prev: any[]) => crewWithoutCharterer(prev));
  };

  const addCrewMember = () => {
    setCrewMembers([...crewMembers, { name: '', passport: '', dateOfBirth: '', nationality: '', role: 'passenger' }]);
  };

  const removeCrewMember = (index: number) => {
    setCrewMembers(crewMembers.filter((_, i) => i !== index));
  };

  // S3 helpers (Crew List πίνακας)
  const crewRoleLabel = (role: string) => role === 'skipper' ? t.ca_skipper : role === 'co-skipper' ? t.ca_coSkipper : role === 'crew' ? t.ca_crewMember : t.ca_passenger;
  // ΣΚΙΠΕΡ = ΝΑΥΛΩΤΗΣ — ΑΥΤΟΜΑΤΗ ΑΝΑΓΝΩΡΙΣΗ. Ταιριάζει ΜΟΝΟ όταν συμπίπτουν
  // ΟΝΟΜΑ **ΚΑΙ** ΤΑΥΤΟΤΗΤΑ/ΔΙΑΒΑΤΗΡΙΟ. ΠΟΤΕ μόνο με όνομα: δύο συνονόματοι με
  // διαφορετικό ID είναι διαφορετικοί άνθρωποι. Είναι ΜΟΝΟ υπολογισμός εμφάνισης
  // — καμία εγγραφή, ο χρήστης κρατά ό,τι διάλεξε στο dropdown.
  const skipperSameAsChartererAuto: boolean = (() => {
    const c: any = chartererData || {};
    const skRow: any = (crewMembers || []).find((m: any) => String(m?.role || '').toLowerCase() === 'skipper') || {};
    const chName = normName(c.firstName, c.lastName) || normName(c.name);
    const skName = normName(c.skipperFirstName, c.skipperLastName) || normName(c.skipperName) || normName(skRow.name, skRow.firstName, skRow.lastName);
    // ΣΥΝΟΛΑ ταυτοτήτων: ο ναυλωτής μπορεί να έχει ID Card και ο σκίπερ Passport.
    const chIds = idKeys(c.idNumber, c.passport, c.chartererIdNumber, c.chartererPassport);
    const skIds = idKeys(c.skipperIdNumber, c.skipperPassport, skRow.passport, skRow.idNumber);
    return samePersonStrict(chName, chIds, skName, skIds);
  })();
  // Ο σκίπερ ΕΙΝΑΙ ο ναυλωτής: είτε το δήλωσε ο χρήστης ('me'), είτε το δείχνουν
  // τα ίδια στοιχεία. Και στις δύο περιπτώσεις ΔΕΝ πιάνει θέση «Passenger 1».
  const skipperIsCharterer: boolean = skipperMode === 'me' || skipperSameAsChartererAuto;
  // BUG D: αρίθμηση. Ορατά crew = ό,τι ΤΥΠΩΝΕΤΑΙ στον πίνακα (χωρίς κενά). Ο ναυλωτής
  // βγαίνει από τη μέτρηση ΜΟΝΟ όταν τη θέση του την κρατά η synthetic γραμμή — αν τον
  // δείχνει η δική του γραμμή crew (792), είναι κανονικός επιβαίνων και ΜΕΤΡΑΕΙ.
  const crewIsReal = (m: any) => !(showChartererSyntheticRow && isChartererMember(m)) && (String(m.name || '').trim() !== '' || String(m.passport || '').trim() !== '');
  const memberRoleLabel = (member: any, index: number) => {
    const r = member.role || 'passenger';
    if (r === 'skipper') return t.ca_skipper;
    if (r === 'co-skipper') return t.ca_coSkipper;
    const countUpTo = (crewMembers || []).slice(0, index + 1).filter((x: any) => crewIsReal(x) && (x.role || 'passenger') === r).length;
    if (r === 'crew') return t.ca_crewMember + ' ' + countUpTo;
    // Το «Passenger 1» το πιάνει ΜΟΝΟ η synthetic γραμμή του ναυλωτή, κι αυτή μόνο όταν
    // υπάρχει ΚΑΙ δεν είναι ο σκίπερ. Χωρίς synthetic γραμμή (ο ναυλωτής είναι ήδη μέσα
    // στο crew, 792) οι επιβάτες μετρούν από 1 — αλλιώς έμενε τρύπα στο «Passenger 1».
    const base = (showChartererSyntheticRow && !skipperIsCharterer) ? 1 : 0;
    return t.ca_passenger + ' ' + (base + countUpTo);
  };
  // S4a: label + κόκκινο Required (μοτίβο Boatjump). Δεν αλλάζει ΤΙ είναι required — μόνο εμφάνιση.
  const reqLabel = (name: string) => (
    <label className="text-sm font-semibold text-gray-700 mb-1 block">{name} <span className="cp-required">{REQ[language] || REQ.en}</span></label>
  );
  const crewInviteFor = (m: any) => (m && m.email) ? invitations.find((inv: any) => (inv.invite_email || '').toLowerCase() === (m.email || '').toLowerCase()) : null;
  const editChartererRow = () => { setShowChartererForm(true); setTimeout(() => document.getElementById('cp-charterer-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60); };
  // S5: helpers σύνοψης
  const scrollToId = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  // S3.5: τοπικοποιημένη ημερομηνία
  const fmtDate = (d: any) => { if (!d) return ''; try { const dt = new Date(d); return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString(language, { day: '2-digit', month: 'short', year: 'numeric' }); } catch (e) { return String(d); } };
  // Fix διπλού skipper: το crew member που είναι ο ίδιος ο charterer (auto-row) δεν εμφανίζεται ξανά.
  const isChartererMember = (m: any) => {
    if (!m) return false;
    if (m.isCharterer === true) return true;
    const norm = (x: any) => String(x == null ? '' : x).trim().toLowerCase().replace(/\s+/g, ' ');
    // ΟΝΟΜΑ: σύνολο λέξεων (normName) — το Crew List γράφει ΕΠΩΝΥΜΟ-ΟΝΟΜΑ, η φόρμα
    // ΟΝΟΜΑ-ΕΠΩΝΥΜΟ. ID: normId (ομόγλυφα/κενά έξω). Χρειάζονται ΚΑΙ ΤΑ ΔΥΟ.
    const cName = normName(chartererData.firstName, chartererData.lastName);
    const cPass = normId(chartererData.idNumber || (chartererData as any).passport);
    const cEmail = norm(chartererData.email);
    const mEmail = norm(m.email);
    if (cEmail && mEmail && cEmail === mEmail) return true;
    if (cName && normName(m.name) === cName && cPass && normId(m.passport) === cPass) return true;
    return false;
  };

  // Ο ναυλωτής είναι ΗΔΗ μέσα στα crewMembers; Ταίριασμα με name Ή passport (πιο χαλαρό
  // από isChartererMember που θέλει ΚΑΙ τα δύο) — π.χ. AI extraction βάζει τον main booker
  // ως γραμμή skipper. Επιστρέφει index ή -1.
  const chartererCrewIdx: number = (() => {
    const cName = normName(chartererData.firstName, chartererData.lastName);
    const cPass = normId(chartererData.idNumber || (chartererData as any).passport);
    if (!cName && !cPass) return -1; // δεν έχει ταυτότητα ακόμη → τίποτα να ταιριάξει
    return (crewMembers || []).findIndex((m: any) =>
      (m && m.isCharterer === true) ||
      (cPass && normId(m && m.passport) === cPass) ||
      (cName && normName(m && m.name) === cName)
    );
  })();
  // Έχει ο ναυλωτής ΚΑΠΟΙΑ ταυτότητα (όνομα ή passport); Αν όχι, η hardcoded γραμμή είναι
  // καθαρό phantom (όλο παύλες) και ΔΕΝ πρέπει να ρεντάρεται.
  const chartererHasIdentity: boolean =
    String(`${chartererData.firstName || ''}${chartererData.lastName || ''}`).trim() !== '' ||
    String(chartererData.idNumber || (chartererData as any).passport || '').trim() !== '';
  // Δείξε τη synthetic γραμμή ΜΟΝΟ όταν ο ναυλωτής ΔΕΝ είναι στα crewMembers ΚΑΙ έχει
  // ταυτότητα. Αλλιώς: τον δείχνει η πραγματική γραμμή του (match) ή είναι κενός (phantom).
  /* ═══ Η ΕΤΑΙΡΕΙΑ ΔΕΝ ΕΙΝΑΙ ΕΠΙΒΑΤΗΣ — ΟΥΤΕ ΣΤΗΝ ΟΘΟΝΗ (19/09/2026) ═══════
     Η «synthetic» γραμμή δείχνει τον ναυλωτή μέσα στο Crew List ακόμη κι όταν
     ΔΕΝ είναι αποθηκευμένος ως μέλος. Με εταιρικό ναυλωτή αυτό έβγαζε
     «Sail and Stay Prive GmbH (Charterer) — Passenger 1» στο online Fill Crew
     Details: ανύπαρκτος επιβάτης, με παύλες σε διαβατήριο/γέννηση/φύλο, που
     μετρούσε κιόλας στο πλήθος. Νομικό πρόσωπο δεν επιβαίνει: η γραμμή ΔΕΝ
     ζωγραφίζεται και η αρίθμηση ξεκινά από «Passenger 1» στον πρώτο ΑΝΘΡΩΠΟ
     (η βάση αρίθμησης διαβάζει την ίδια μεταβλητή). */
  const showChartererSyntheticRow: boolean = chartererHasIdentity && chartererCrewIdx < 0 && !chartererIsCompany;
  // Λείπουν στοιχεία σκίπερ; ΤΑ ΙΔΙΑ πεδία που παλιά μπλόκαραν το submit — τώρα μόνο
  // τροφοδοτούν την ένδειξη αναμονής στη Σύνοψη.
  const skipperDataPending = () => {
    // ΔΗΛΩΜΕΝΟ 'me' Ή ΑΝΑΓΝΩΡΙΣΜΕΝΟ (ίδιο όνομα+ταυτότητα, ή ίδιο όνομα με ναυλωτή
    // χωρίς ID): ο σκίπερ ΕΙΝΑΙ ο ναυλωτής — καμία «αναμονή στοιχείων».
    if (skipperIsCharterer) return false;
    if (skipperMode === 'me') return false;
    if (skipperMode === 'crew') return !(crewMembers || []).some((m: any) => String(m?.role || '').toLowerCase() === 'skipper');
    const c: any = chartererData; // 'other' | 'invite'
    return !c.skipperFirstName || !c.skipperLastName || !c.skipperGen || !c.skipperDob || !c.skipperNationality;
  };

  const skipperSummaryText = () => {
    const S = SUMMARY_T[language] || SUMMARY_T.en;
    // Ο σκίπερ είναι ο ναυλωτής — δηλωμένο Ή αναγνωρισμένο. ΠΟΤΕ «Άλλο πρόσωπο»/«Αναμονή».
    if (skipperIsCharterer) {
      const nm = `${chartererData.firstName || ''} ${chartererData.lastName || ''}`.trim()
        || `${chartererData.skipperFirstName || ''} ${chartererData.skipperLastName || ''}`.trim()
        || (crewMembers || []).find((m: any) => String(m?.role || '').toLowerCase() === 'skipper')?.name
        || '—';
      return nm + ' (' + S.charterer + ')';
    }
    if (skipperMode === 'invite') return S.viaInvite;
    if (skipperMode === 'crew') { const s: any = (crewMembers || []).find((m: any) => m.role === 'skipper'); return (s && s.name) ? s.name : '—'; }
    return `${chartererData.skipperFirstName || ''} ${chartererData.skipperLastName || ''}`.trim() || '—';
  };

  // Ποια ΥΠΟΧΡΕΩΤΙΚΑ πεδία ναυλωτή λείπουν — ΕΝΔΕΙΞΗ στη Σύνοψη (ΔΕΝ μπλοκάρει το submit,
  // ίδια φιλοσοφία με skipperDataPending). Οι ετικέτες είναι ήδη μεταφρασμένες μέσω t.*.
  const chartererMissing = (): string[] => {
    if (!showChartererForm) return [];
    const c: any = chartererData;
    const G = (GEN_LABEL[language] || GEN_LABEL.en);
    const miss: string[] = [];
    /* ΕΤΑΙΡΕΙΑ: επωνυμία, ΑΦΜ/VAT, επικοινωνία — ΟΧΙ φύλο/γέννηση/εθνικότητα/
       διαβατήριο· νομικό πρόσωπο δεν έχει τίποτα από αυτά. */
    if (chartererIsCompany) {
      if (!c.companyName && !c.lastName) miss.push(t.ca_companyName || 'Επωνυμία');
      if (!c.taxNumber) miss.push(t.ca_taxNumberAfm);
      if (!c.tel) miss.push(t.ca_phoneShort);
      if (!c.email) miss.push(t.ca_emailLabel);
      return miss;
    }
    if (!c.firstName) miss.push(t.ca_firstName);
    if (!c.lastName) miss.push(t.ca_lastName);
    if (!c.idNumber) miss.push(t.ca_idOrPassportNum);
    if (!c.tel) miss.push(t.ca_phoneShort);
    if (!c.email) miss.push(t.ca_emailLabel);
    if (!c.gender) miss.push(G);
    if (!c.dob) miss.push(t.ca_dateOfBirth);
    if (!c.nationality) miss.push(t.ca_nationality);
    return miss;
  };

  const handleCrewMemberChange = (index: number, field: string, value: string) => {
    const updated = [...crewMembers];
    updated[index] = { ...updated[index], [field]: value };
    setCrewMembers(updated);
    // Αν αλλάζει ο ρόλος του charterer (isCharterer) row, συγχρόνισε το isAlsoSkipper
    if (field === 'role' && updated[index] && updated[index].isCharterer) {
      const becomesSkipper = value === 'skipper';
      setChartererData((prev: any) => {
        if (becomesSkipper) {
          return { ...prev, isAlsoSkipper: true,
            skipperFirstName: prev.firstName || '', skipperLastName: prev.lastName || '',
            skipperName: `${prev.firstName || ''} ${prev.lastName || ''}`.trim(),
            skipperPassport: prev.idNumber || prev.passport || '', skipperNationality: prev.nationality || '',
            skipperDob: prev.dob || '', skipperGen: prev.gender || '', skipperEmail: prev.email || '', skipperPhone: prev.tel || '' };
        }
        return { ...prev, isAlsoSkipper: false };
      });
    }
  };

  // 🔥 FIX 26: Updated to save crew to API
  const handleSubmit = async () => {
    if (!bookingData) {
      alert(t.ca_alertNoBookingExcl);
      return;
    }

    // ΑΝΤΙΣΤΡΟΦΟΣ ΚΑΘΡΕΦΤΗΣ ΣΤΟ SAVE (όχι μόνο on-change): σιγουρεύει ότι τα στοιχεία σκίπερ από τη
    // φόρμα (chartererData.skipper*) βρίσκονται στη γραμμή crewMembers[role='skipper'] ΠΡΙΝ τρέξει το
    // validation (:~1351) και το build του crewToSave — αλλιώς το state από τον on-change effect μπορεί
    // να μην έχει προλάβει να κάνει commit και το submit θα κοβόταν/θα χανόταν. Empty-only → δεν πατάει
    // authoritative crew data. Τα edits κάτω χρησιμοποιούν workingCrew αντί για το raw crewMembers.
    let workingCrew = crewMembers;
    {
      const skIdx = (crewMembers || []).findIndex((m: any) => String(m?.role || '').toLowerCase() === 'skipper');
      if (skIdx >= 0 && skipperMode !== 'me') {
        const rec: any = crewMembers[skIdx];
        const patch: any = {};
        const fill = (crewKey: string, flatVal: any) => {
          if (!String(rec[crewKey] ?? '').trim() && String(flatVal ?? '').trim()) patch[crewKey] = flatVal;
        };
        fill('passport', latinId(chartererData.skipperIdNumber)); // ID→ομόγλυφα (ΑΡ→AP)
        fill('gender', chartererData.skipperGen);
        fill('dateOfBirth', chartererData.skipperDob);
        fill('nationality', chartererData.skipperNationality);
        // Επέκταση (ίδια με on-change): επικοινωνία/ταυτότητα Fleet flat → crew skipper.
        fill('email', chartererData.skipperEmail);
        fill('phone', chartererData.skipperPhone);
        fill('address', chartererData.skipperAddress);
        fill('taxNumber', chartererData.skipperTaxNumber);
        fill('taxOffice', chartererData.skipperTaxOffice);
        fill('firstName', toLatin(chartererData.skipperFirstName));
        fill('lastName', toLatin(chartererData.skipperLastName));
        fill('name', toLatin(chartererData.skipperName || `${chartererData.skipperFirstName || ''} ${chartererData.skipperLastName || ''}`.trim()));
        if (Object.keys(patch).length) {
          workingCrew = crewMembers.map((m: any, i: number) => i === skIdx ? { ...m, ...patch } : m);
          setCrewMembers(workingCrew); // κράτα και το UI/Σύνοψη σε συγχρονισμό
        }
      }
    }

    // Skipper license upload is now OPTIONAL — no blocking check (upload UI remains available).
    // Downstream usage of skipperLicense is if-guarded, so an empty value is safe.

    // Πλήρωμα: passport, ΦΥΛΟ, ΗΜ. ΓΕΝΝΗΣΗΣ, ΕΘΝΙΚΟΤΗΤΑ ΥΠΟΧΡΕΩΤΙΚΑ για κάθε μέλος με όνομα
    // (εξαιρείται ο charterer — ελέγχεται στην ενότητά του· κενές γραμμές αγνοούνται).
    if (showCrewForm && workingCrew.some((m: any) => !isChartererMember(m) && String(m.name || '').trim() !== '' && (!m.passport || !m.gender || !m.dateOfBirth || !m.nationality))) {
      alert(t.ca_alertFillCrewAll);
      return;
    }

    // ΝΑΥΛΩΤΗΣ: ΔΕΝ μπλοκάρει πλέον το submit (εντολή Kostas — ίδια φιλοσοφία με το skipper-block
    // που βγάλαμε στο v6.464). Το submit περνά ΠΑΝΤΑ. Τι λείπει φαίνεται ως ΕΝΔΕΙΞΗ στη Σύνοψη μέσω
    // chartererMissing() — ειδοποιεί, δεν εμποδίζει. Τα στοιχεία είναι επεξεργάσιμα μέχρι το check-in.

    const pendingInv = invitations.filter((i: any) => i.status === 'pending');
    if (pendingInv.length > 0) {
      const names = pendingInv.map((i: any) => (i.invite_first_name || i.invite_email)).join(', ');
      const proceed = window.confirm('Awaiting crew/skipper completion from: ' + names + '. Continue and submit without these?');
      if (!proceed) return;
    }
    const bookingCode = bookingData.bookingCode;
    // Base crew: existing/loaded rows + edits. Drop blank non-self placeholder rows
    // so the default empty row isn't persisted; keep the self row even if blank.
    const baseCrew = [...workingCrew].filter((m: any) =>
      m && (m.isCharterer === true || String(m.name || '').trim() !== '' || String(m.passport || '').trim() !== '')
    );
    // Ο charterer είναι ΕΝΑ crew entry ("self"). Όταν είναι skipper → εκπροσωπείται ξεχωριστά
    // (DOCX CREW1 από skipperName + auto-row στην οθόνη), οπότε ΔΕΝ αποθηκεύουμε διπλό crew member.
    // Όταν είναι passenger → παραμένει ως το μοναδικό self crew entry (το χρειάζεται το DOCX).
    const selfIsSkipper = chartererData.isAlsoSkipper === true || bookingData?.sameAsSkipper === true || bookingData?.sameAsSkipper === 'true';
    let finalCrew: any[];
    /* ═══ Η ΕΤΑΙΡΕΙΑ ΔΕΝ ΤΑΞΙΔΕΥΕΙ (19/09/2026) ══════════════════════════════
       Με εταιρικό ναυλωτή ΔΕΝ γράφεται γραμμή «εγώ» στο πλήρωμα: ένα νομικό
       πρόσωπο δεν επιβαίνει, δεν έχει διαβατήριο και δεν δηλώνεται στην ΑΑΔΕ ως
       επιβάτης. Η λίστα γεμίζει ΜΟΝΟ από σκίπερ/μέλη που δηλώνονται ρητά. */
    if (chartererIsCompany) {
      finalCrew = crewWithoutCharterer(baseCrew);
    } else if (selfIsSkipper) {
      finalCrew = baseCrew.filter((m: any) => !isChartererMember(m));
    } else {
      const selfEntry = {
        name: `${chartererData.firstName || ''} ${chartererData.lastName || ''}`.trim(),
        passport: chartererData.idNumber || (chartererData as any).passport || '',
        dateOfBirth: (chartererData as any).dob || '',
        nationality: (chartererData as any).nationality || '',
        gender: (chartererData as any).gender || '',
        email: chartererData.email || '',
        phone: chartererData.tel || '',
        role: 'passenger',
        isCharterer: true,
      };
      const selfIdx = baseCrew.findIndex((m: any) => m && m.isCharterer === true);
      if (selfIdx >= 0) {
        const existingSelf: any = baseCrew[selfIdx] || {};
        const mergedSelf: any = { ...existingSelf };
        Object.keys(selfEntry).forEach((kk) => {
          const v = (selfEntry as any)[kk];
          if (kk === 'isCharterer' || kk === 'role') { mergedSelf[kk] = v; return; }
          if (v !== undefined && v !== null && String(v).trim() !== '') { mergedSelf[kk] = v; }
          else if (mergedSelf[kk] === undefined || mergedSelf[kk] === null || String(mergedSelf[kk]).trim() === '') { mergedSelf[kk] = v; }
        });
        baseCrew[selfIdx] = mergedSelf;
      } else if (String(selfEntry.name || '').trim() !== '') {
        baseCrew.unshift(selfEntry);
      }
      finalCrew = baseCrew;
    }
    const crewToSave = dedupeCrew(unifyCrew(finalCrew)); // unifyCrew: όνομα/ID→λατινικά + merge ίδιου ID πριν το save

    try {
      // Save crew members to API (API is source of truth - no localStorage)
      /* Με εταιρικό ναυλωτή η λίστα μπορεί να ΑΔΕΙΑΣΕΙ νόμιμα (έφυγε η γραμμή
         «εγώ»): τότε ΠΡΕΠΕΙ να σταλεί για να φύγει και από τον server. */
      const crewHadCharterer = (workingCrew || []).some((m: any) => m && m.isCharterer === true);
      if (crewToSave.length > 0 || (chartererIsCompany && crewHadCharterer)) {
        console.log('👥 Saving crew to API...', { bookingCode, crewMembers: crewToSave });
        await updateCharterCrew(bookingCode, crewToSave);
        crewDirtyRef.current = false; // αποθηκεύτηκε → το poll ξαναεπιτρέπεται
      }

      // Save charterer data with legal timestamps
      /* ΕΤΑΙΡΕΙΑ: δεν υπάρχει firstName — η συνθήκη έκοβε ΟΛΗ την αποθήκευση του
         ναυλωτή. Κριτήριο είναι το ΟΝΟΜΑ, όποιο σχήμα κι αν έχει. */
      const chartererHasName = chartererIsCompany
        ? !!String(chartererData.companyName || chartererData.lastName || '').trim()
        : !!chartererData.firstName;
      if (showChartererForm && chartererHasName) {
        const now = new Date().toISOString();
        const chartererToSave: any = {
          ...(chartererIsCompany ? stripIndividualFields(chartererData) : chartererData),
          ...(chartererIsCompany ? { isCompany: true, isAlsoSkipper: false } : {}),
          skipperMode: skipperMode, // ρητή αποθήκευση επιλογής dropdown (nested)
          submittedAt: chartererData.submittedAt || now,
          lastModifiedAt: now,
          // signedAt fallback: αν δεν tracκάρεται από stopDrawing (legacy)
          // αλλά υπάρχει υπογραφή → χρησιμοποίησε το current submit timestamp.
          chartererSignedAt: chartererData.chartererSignedAt || (chartererData.signature ? now : ''),
          skipperSignedAt: chartererData.skipperSignedAt || (chartererData.skipperSignature ? now : ''),
          submittedUserAgent: (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '',
          isLocked: true
        };
        // SAFETY: charterer = skipper → derive skipper* from charterer before sending (idempotent).
        // Covers stale state where the checkbox copy didn't run. Sends the FULL object to both endpoints.
        if (chartererToSave.isAlsoSkipper) {
          chartererToSave.skipperFirstName = chartererData.firstName || '';
          chartererToSave.skipperLastName = chartererData.lastName || '';
          chartererToSave.skipperName = `${chartererData.firstName || ''} ${chartererData.lastName || ''}`.trim();
          chartererToSave.skipperAddress = chartererData.address || '';
          chartererToSave.skipperPhone = chartererData.tel || '';
          chartererToSave.skipperEmail = chartererData.email || '';
          chartererToSave.skipperIdType = chartererData.idType || 'id';
          chartererToSave.skipperIdNumber = chartererData.idNumber || '';
          chartererToSave.skipperTaxNumber = chartererData.taxNumber || '';
          chartererToSave.skipperTaxOffice = chartererData.taxOffice || '';
          chartererToSave.skipperDob = chartererData.dob || '';
          chartererToSave.skipperNationality = chartererData.nationality || '';
          chartererToSave.skipperGen = chartererData.gender || '';
        }
        console.log('📋 Saving charterer data to API...', { bookingCode });
        await saveChartererData(bookingCode, chartererToSave, isAdmin() ? getAdminCode() : undefined);
        setChartererData(chartererToSave);
        chartererDirtyRef.current = false; // αποθηκεύτηκε → το poll ξαναεπιτρέπεται

        // SAVE_BACK_TO_BOOKING_MARKER
        try {
          console.log('🔄 Syncing charterer data back to bookings table...');
          // v6.473 — ΡΗΤΗ ΔΙΑΓΡΑΦΗ ΣΚΙΠΕΡ: μόνο αν ο ΙΔΙΟΣ ο χρήστης άδειασε πεδία σε αυτή τη
          // συνεδρία ΚΑΙ όλα τα skipper* είναι πλέον κενά. Το «κενό state» από μόνο του ΔΕΝ
          // σβήνει τίποτα (προστασία ναύλων με γεμάτο flat & κενό Agreement).
          const skAllEmpty = ['skipperFirstName','skipperLastName','skipperName','skipperEmail','skipperPhone',
            'skipperAddress','skipperIdNumber','skipperDob','skipperGen','skipperNationality']
            .every((k) => String((chartererToSave as any)[k] ?? '').trim() === '');
          const skipperCleared = skipperUserClearedRef.current && skipperMode !== 'me' && skipperMode !== 'invite' && skAllEmpty;
          await updateCharterCrew(bookingCode, { chartererData: chartererToSave, ...(skipperCleared ? { skipperCleared: true } : {}) } as any);
          console.log('✅ Bookings table synced with charterer data');
        } catch (syncErr) {
          console.log('⚠️ Bookings sync failed (charter_archives save succeeded):', syncErr);
        }
      }

      alert(t.ca_submitSuccess);

      // Save uploaded documents to Charter Archive
      const today = new Date().toLocaleDateString('el-GR');
      const documents: any = {};
      if (skipperLicense) {
        documents.skipperLicense = [{ name: 'Skipper License', dataUrl: skipperLicense, uploadDate: today }];
        extraLicenses.forEach((lic, i) => documents.skipperLicense.push({ name: 'Skipper License ' + (i+2), dataUrl: lic, uploadDate: today }));
      }
      if (charterPartyScan) {
        documents.charterAgreement = [{ name: 'Charter Party Scan', dataUrl: charterPartyScan, uploadDate: today }];
      }
      if (crewListScan) {
        documents.crewList = [{ name: 'Crew List Scan', dataUrl: crewListScan, uploadDate: today }];
      }
    if (Object.keys(documents).length > 0) {
      // Fetch existing docs and merge with new ones
      let mergedDocs = { ...documents };
      try {
        const existResp = await fetch('/api/charter-archive.php?booking_number=' + encodeURIComponent(bookingCode));
        if (existResp.ok) {
          const existData = await existResp.json();
          const existingDocs = existData?.data?.documents || {};
          mergedDocs = { ...existingDocs, ...documents };
          console.log('Merged docs:', mergedDocs);
        }
      } catch (e) { console.log('No existing archive to merge'); }

      console.log('\ud83d\udcc1 Saving merged documents to archive...', { bookingCode, mergedDocs });
      await fetch('/api/charter-archive.php', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ booking_number: bookingCode, archive_data: { documents: mergedDocs } })
      });
    }

    // Send notification emails
      try {
        const docsUploaded = [];
        if (skipperLicense || extraLicenses.length > 0) docsUploaded.push('Skipper License (' + (1 + extraLicenses.length) + ')');
        if (charterPartyScan) docsUploaded.push('Charter Party Scan');
        if (crewListScan) docsUploaded.push('Crew List Scan');
        const hasOnlineCrew = crewToSave.length > 0;
        const vesselName = bookingData?.vesselName || '';
        const clientName = ((bookingData?.chartererFirstName || '') + ' ' + (bookingData?.chartererLastName || '')).trim() || bookingData?.chartererName || bookingData?.clientName || '';
        const startDate = bookingData?.startDate || '';
        const endDate = bookingData?.endDate || '';
        const clientEmail = bookingData?.chartererEmail || bookingData?.skipperEmail || '';

        const crewTable = hasOnlineCrew ? '<h3>Online Crew List (' + crewToSave.length + ' members):</h3><table border="1" cellpadding="8" style="border-collapse:collapse;width:100%"><tr style="background:#1e40af;color:white"><th>Name</th><th>Passport</th><th>DOB</th><th>Nationality</th><th>Email</th><th>Phone</th></tr>' + crewToSave.map((m: any) => '<tr><td>' + (m.name||'') + '</td><td>' + (m.passport||'') + '</td><td>' + formatDateEU(m.dateOfBirth) + '</td><td>' + (m.nationality||'') + '</td><td>' + (m.email||'') + '</td><td>' + (m.phone||'') + '</td></tr>').join('') + '</table>' : '';
        const docsListHtml = docsUploaded.map(d => '<li style="color:#16a34a;padding:4px 0">\u2705 ' + d + '</li>').join('');

        // EMAIL 1: Admin notification
        const adminHtml = '<div style="font-family:Arial,sans-serif;max-width:700px;margin:0 auto"><div style="background:#1e40af;color:white;padding:20px;border-radius:8px 8px 0 0;text-align:center"><h1>\u2693 Charter Agreement Submitted</h1></div><div style="padding:20px;background:#f8fafc;border:1px solid #e2e8f0"><h2>' + bookingCode + '</h2><p><strong>Vessel:</strong> ' + vesselName + '</p><p><strong>Client:</strong> ' + clientName + '</p><p><strong>Client Email:</strong> ' + clientEmail + '</p><p><strong>Dates:</strong> ' + startDate + ' - ' + endDate + '</p><hr style="margin:15px 0"><h3>Documents Uploaded:</h3>' + (docsUploaded.length > 0 ? '<ul style="list-style:none;padding:0">' + docsListHtml + '</ul>' : '<p style="color:#dc2626">\u274c No documents uploaded</p>') + (hasOnlineCrew ? '<p style="color:#16a34a">\u2705 Online Crew List (' + crewToSave.length + ' members)</p>' : '') + crewTable + '<hr style="margin:15px 0"><p style="color:#64748b;font-size:12px">Submitted: ' + new Date().toLocaleString('el-GR') + '</p></div></div>';

        await fetch('https://yachtmanagementsuite.com/email/send-email', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ to: clientEmail ? ['charter@tailwindyachting.com', clientEmail] : 'charter@tailwindyachting.com', subject: '\u2693 Charter Submitted - ' + bookingCode + ' - ' + vesselName, html: adminHtml })
        });

        // EMAIL 2: Client confirmation
        if (clientEmail) {
          const clientHtml = '<div style="font-family:Arial,sans-serif;max-width:600px;margin:0 auto"><div style="background:linear-gradient(135deg,#0ea5e9,#1e40af);color:white;padding:30px;border-radius:12px 12px 0 0;text-align:center"><div style="font-size:40px">\u26f5</div><h1 style="margin:10px 0">Tailwind Yachting</h1></div><div style="padding:25px;background:white;border:1px solid #e2e8f0"><p style="font-size:16px">Dear <strong>' + clientName + '</strong>,</p><p>Thank you for submitting your charter documents for <strong>' + vesselName + '</strong> - <strong>' + bookingCode + '</strong>.</p><table style="width:100%;margin:15px 0;background:#f0f9ff;border-radius:8px;padding:15px"><tr><td><p><strong>Check-in:</strong> ' + startDate + '</p><p><strong>Check-out:</strong> ' + endDate + '</p></td></tr></table><h3>Documents received:</h3><ul style="list-style:none;padding:0">' + docsListHtml + (hasOnlineCrew ? '<li style="color:#16a34a;padding:4px 0">\u2705 Online Crew List (' + crewToSave.length + ' members)</li>' : '') + '</ul><p>We will review your documents and contact you if anything is needed.</p><div style="background:#f0f9ff;border-radius:8px;padding:15px;margin:20px 0"><p><strong>Meeting Point:</strong> Marina Alimos - Charter Village Box A47</p><p><strong>Contact:</strong> Ms Maria Mazaraki (+30 6978196009)</p></div><p style="font-size:16px;text-align:center;margin-top:20px">We look forward to welcoming you aboard! \u26f5</p></div><div style="background:#1e293b;color:white;padding:15px;border-radius:0 0 12px 12px;text-align:center;font-size:12px"><p>Tailwind Yachting | Marina Alimos, Greece</p></div></div>';

          await fetch('https://yachtmanagementsuite.com/email/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ to: clientEmail, subject: '\u26f5 Tailwind Yachting - Documents Received', html: clientHtml })
          });
        }
      } catch(emailErr) { console.log('Email send failed:', emailErr); }

    navigate('/home');
    } catch (error) {
      console.error('❌ Error saving charter agreement data:', error);
      alert(t.ca_errorSaving);
    }
  };

  // S5/2a: license κάρτα ως μεταβλητή → render αμέσως κάτω από το skipper (εκτός [invite]).
  const licenseCard = (
    <div className="bg-white rounded-2xl p-6 shadow-lg ca-section">
      <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-4">
        <span className="text-2xl">📸</span>
        {t.ca_skipperLicense}
      </h2>
      <p className="text-sm text-gray-600 mb-4">{t.ca_skipperLicenseDesc}</p>
      {!skipperLicense && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 mb-4 text-amber-800 font-semibold text-sm flex items-center gap-2">
          ⚠️ {(LICENSE_PENDING[language] || LICENSE_PENDING.en)}
        </div>
      )}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
        {skipperLicense ? (
          <div className="space-y-4">
            {renderLicensePreview(skipperLicense, 'Skipper License')}
            <button onClick={() => setSkipperLicense('')} className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600">🗑️ {t.ca_remove}</button>
            <label className="cursor-pointer inline-block mt-3 px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600">
              + {t.ca_addAnotherLicense}
              <input type="file" accept="image/jpeg,image/png,image/jpg,.pdf,.doc,.docx" className="hidden" onChange={handleSkipperLicenseUpload} />
            </label>
            {extraLicenses.map((lic, idx) => (
              <div key={'extra-' + idx} className="mt-3 space-y-2">
                {renderLicensePreview(lic, 'License ' + (idx + 2))}
                <button onClick={() => setExtraLicenses(prev => prev.filter((_, i) => i !== idx))} className="px-3 py-1 bg-red-500 text-white rounded-lg text-sm hover:bg-red-600">{t.ca_remove}</button>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-col md:flex-row gap-4 justify-center items-center">
              <label className="cursor-pointer flex-1 max-w-xs">
                <div className="p-6 border-2 border-blue-400 rounded-lg hover:bg-blue-50 transition-colors">
                  <div className="text-5xl mb-2">📷</div>
                  <p className="text-lg font-semibold text-gray-700">{t.ca_takePhoto}</p>
                  <p className="text-sm text-gray-500 mt-1 cp-helper">{t.ca_useCamera}</p>
                </div>
                <input type="file" accept="image/*,.pdf,.doc,.docx" capture="environment" onChange={handleCameraCapture} className="hidden" />
              </label>
              <label className="cursor-pointer flex-1 max-w-xs">
                <div className="p-6 border-2 border-gray-400 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="text-5xl mb-2">📁</div>
                  <p className="text-lg font-semibold text-gray-700">{t.ca_uploadFile}</p>
                  <p className="text-sm text-gray-500 mt-1 cp-helper">{t.ca_chooseGallery}</p>
                </div>
                <input type="file" accept="image/*,.pdf,.doc,.docx" onChange={handleSkipperLicenseUpload} className="hidden" />
              </label>
            </div>
            <p className="text-sm text-gray-500 cp-helper">{t.ca_orDragDrop}</p>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <>
      <style>{}</style>
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-blue-50 to-slate-200 p-6 cp-theme">
      
      <header className="bg-white shadow-md rounded-2xl p-6 mb-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <div className="text-4xl">⚓</div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t.ca_title}
              </h1>
              {bookingData && (
                <>
                  <p className="text-sm text-gray-600">
                    {t.ca_booking}: <span className="font-bold text-blue-600">{bookingData.bookingCode}</span>
                  </p>
                  {/* S3.5: σκάφος/ημερομηνίες μεταφέρθηκαν στην κάρτα ναύλου (πρώτη ενότητα). */}
                  <div className="flex items-center">
                    <p className="text-xs text-gray-400">
                      {t.ca_lastUpdated}: {lastUpdated.toLocaleTimeString()}
                    </p>
                    {isRefreshing && (
                      <span className="ml-3 px-3 py-1 bg-blue-100 text-blue-700 text-sm rounded-full animate-pulse">
                        🔄 {t.ca_updating}
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <button
            onClick={() => navigate('/charter-guide?lang=' + language)}
            style={{
              background: 'rgba(59, 130, 246, 0.15)',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '20px',
              padding: '8px 20px',
              color: '#1e40af',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              marginRight: '8px',
            }}
          >
            📖 {(HELP_TEXTS[language] || HELP_TEXTS.en).helpButton}
          </button>

          {currentEmployee && (
            <div style={{display:'inline-flex', alignItems:'center', gap:8, padding:'8px 12px', background:'#fff3cd', borderRadius:8, marginLeft:8, marginRight:8}}>
              <input
                type="text"
                value={adminBookingInput}
                onChange={(e) => setAdminBookingInput(e.target.value)}
                placeholder="Booking #"
                style={{width:100, padding:'4px 8px', border:'1px solid #999', borderRadius:4, fontSize:14}}
              />
              <button
                onClick={() => {
                  if (!adminBookingInput.trim()) return;
                  sessionStorage.setItem('charterAgreementAdminOverride', adminBookingInput.trim());
                  window.location.reload();
                }}
                style={{padding:'4px 12px', background:'#3b82f6', color:'white', border:'none', borderRadius:4, cursor:'pointer', fontSize:14}}
              >
                Άνοιγμα
              </button>
              {isAdminMode && (
                <>
                  <span style={{color:'#856404', fontSize:12, fontWeight:'bold'}}>⚠️ Admin Mode</span>
                  <button
                    onClick={() => {
                      sessionStorage.removeItem('charterAgreementAdminOverride');
                      window.location.reload();
                    }}
                    style={{padding:'4px 8px', background:'#dc2626', color:'white', border:'none', borderRadius:4, cursor:'pointer', fontSize:12}}
                  >
                    Έξοδος
                  </button>
                </>
              )}
            </div>
          )}

          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setShowLangPopup(!showLangPopup)}
              className="px-4 py-2 bg-blue-100 hover:bg-blue-200 rounded-lg font-semibold text-blue-900 transition-colors flex items-center gap-2"
            >
              <img src={flagImg(LANG_MAP.find((l: any) => l.code === language)?.country || 'GB')} alt="" style={{ width: '24px', height: '18px' }} />
              {language.toUpperCase()}
            </button>
            {showLangPopup && (
              <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setShowLangPopup(false)}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '24px', maxWidth: '340px', width: '90%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }} onClick={(e) => e.stopPropagation()}>
                  <h3 style={{ textAlign: 'center', marginBottom: '16px', fontSize: '18px', color: '#1e293b' }}>{t.ca_selectLanguage}</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
                    {LANG_MAP.map(({ code, country, label }: any) => (
                      <button key={code} onClick={() => { setLanguage(code); setShowLangPopup(false); }} style={{ padding: '12px 8px', borderRadius: '10px', border: 'none', background: language === code ? 'linear-gradient(135deg, #0c4a6e, #0ea5e9)' : '#f1f5f9', color: language === code ? 'white' : '#334155', fontSize: '15px', fontWeight: language === code ? 700 : 500, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                        <img src={flagImg(country)} alt="" style={{ width: '24px', height: '18px', borderRadius: '2px' }} />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </header>

      {!bookingData ? (
        <div className="bg-white rounded-2xl p-8 shadow-lg text-center ca-section">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            {t.ca_noBookingFound}
          </h2>
          <p className="text-gray-600 mb-6">
            {t.ca_alertEnterCode}
          </p>
          <button
            onClick={() => navigate('/home')}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            {('🏠 ' + t.ca_backToHome)}
          </button>
        </div>
      ) : (
        <div className="max-w-4xl mx-auto space-y-6">

          {/* Boarding Pass αφαιρέθηκε από εδώ (S1, Επιλογή A): UI κάρτα + inline functions.
              Το boarding pass ζει στο Documents (DocumentsManager → services/boardingPassService.ts). */}


          {/* Charter Party & Crew List Downloads - HIDDEN (set true to show) */}
          {false && (
          <div className="bg-white rounded-2xl p-6 shadow-lg ca-section">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-2">
              <span className="text-2xl">📋</span>
              {t.ca_documents}
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {t.ca_charterDocsDesc}
            </p>
            <button
              onClick={handleDownloadCharterParty}
              className="w-full font-bold py-3 px-4 rounded-lg mb-2 flex items-center justify-center border transition-all shadow-md bg-indigo-600 hover:bg-indigo-700 text-white border-indigo-500"
            >
              <span className="ml-2">📄 {t.ca_charterPartyDocx}</span>
            </button>
            <p className="text-xs text-gray-400 mb-4 text-center">
              {t.ca_charterAgreementDesc}
            </p>
            <button
              onClick={handleDownloadCrewList}
              className="w-full font-bold py-3 px-4 rounded-lg mb-2 flex items-center justify-center border transition-all shadow-md bg-green-600 hover:bg-green-700 text-white border-green-500"
            >
              <span className="ml-2">👥 {t.ca_crewListDocx}</span>
            </button>
            <p className="text-xs text-gray-400 text-center">
              {t.ca_crewListOfficialDesc}
            </p>
          </div>
          )}

<div className="bg-white rounded-2xl p-6 shadow-lg ca-section">
            {/* S3.5: κάρτα ναύλου (αντικαθιστά το ορφανό κουτί — σκάφος + Charter No + ημερομηνίες) */}
            {(() => { const H = HEADER_T[language] || HEADER_T.en; return (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="text-xl font-bold" style={{ color: 'var(--cp-primary)' }}>🛥️ {bookingData.vesselName || '—'}</div>
                <div className="text-sm text-gray-600">{t.ca_booking}: <span className="font-semibold">{bookingData.bookingCode || (bookingData as any).booking_number || ''}</span></div>
                {bookingData.startDate && <div className="text-sm text-gray-700">📅 {H.departure}: <span className="font-semibold">{fmtDate(bookingData.startDate)}</span></div>}
                {bookingData.endDate && <div className="text-sm text-gray-700">↩︎ {H.ret}: <span className="font-semibold">{fmtDate(bookingData.endDate)}</span></div>}
              </div>
            ); })()}
            {/* 🔥 Financial Summary Section */}
            {bookingData.charterAmount && (
              <div className="mt-4 bg-gradient-to-br from-slate-800 to-gray-900 rounded-xl p-6 text-white">
                <h3 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <span className="text-2xl">💰</span>
                  {t.ca_financialSummary}
                </h3>

                <div className="space-y-3">
                  {/* Charter Amount (NET) - amount entered is GROSS, NET = gross / 1.12 */}
                  <div className="flex justify-between items-center pb-2 border-b border-gray-600">
                    <span className="text-blue-300 font-semibold">
                      {t.ca_charterAmountNet}
                    </span>
                    <span className="text-blue-300 font-bold text-lg">
                      {(parseFloat(bookingData.charterAmount) / 1.12).toFixed(2)}€
                    </span>
                  </div>

                  {/* VAT on Charter (12%) - VAT = gross - NET */}
                  <div className="flex justify-between items-center pb-2 border-b border-gray-600">
                    <span className="text-blue-300 font-medium">
                      {t.ca_vat12}
                    </span>
                    <span className="text-blue-300 font-bold">
                      +{(parseFloat(bookingData.charterAmount) - parseFloat(bookingData.charterAmount) / 1.12).toFixed(2)}€
                    </span>
                  </div>

                  {/* Total Amount - Total = gross (the amount entered by user) */}
                  <div className="flex justify-between items-center pt-2 pb-2 border-b-2 border-teal-500">
                    <span className="text-teal-300 font-bold text-lg">
                      {t.ca_totalWithVat}
                    </span>
                    <span className="text-teal-300 font-bold text-2xl">
                      {parseFloat(bookingData.charterAmount).toFixed(2)}€
                    </span>
                  </div>

                  {/* Deposit Required */}
                  <div className="flex justify-between items-center pt-2 bg-yellow-500/10 rounded-lg px-3 py-2">
                    <span className="text-yellow-300 font-bold">
                      {t.ca_depositRequired}
                    </span>
                    <span className="text-yellow-300 font-bold text-xl">
                      {parseFloat(bookingData.deposit).toFixed(2)}€
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-400 mt-4 italic">
                  {t.ca_fiscalNote}
                </p>
              </div>
            )}

            <div className="text-sm text-gray-500 border-t pt-3 mt-4">
              <p>📄 {t.ca_pdfFormat}</p>
              <p>✍️ {t.ca_printSign}</p>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 shadow-lg ca-section">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-4">
              <span className="text-2xl">👥</span>
  {t.ca_charterParty}
  <span style={{color: '#dc2626', fontWeight: 'bold', marginLeft: '8px', fontSize: '0.85em'}}>ONLINE</span>
          </h2>

          {/* 🔥 NEW: Charter Party Online Form */}
          <div className="space-y-4">
            <div className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-400 transition-colors">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-gray-800">
                    {t.ca_fillCharterPartyOnline}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {t.ca_fillLatinOnly}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowChartererForm(!showChartererForm)}
                  className={`px-6 py-3 rounded-lg font-semibold transition-all shadow-md ${
                    showChartererForm
                      ? 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                  }`}
                >
                  {showChartererForm
                    ? (('❌ ' + t.ca_close))
                    : (('✏️ ' + t.ca_fillNow))}
                </button>
              </div>

              {showChartererForm && (
                <div id="cp-charterer-form" onChangeCapture={() => { chartererDirtyRef.current = true; }} className="bg-gray-50 rounded-lg p-4 space-y-3">
                  {/* Dynamic edit lock (charterer/broker) — admin βλέπει τίποτα από αυτά */}
                  {!isAdmin() && canEdit() && getCharterStartMidnight() && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-2 text-sm text-blue-800">
                      {t.ca_editUntil}{' '}
                      <strong>{getCharterStartMidnight()!.toLocaleDateString('el-GR')}</strong>{' '}
                      00:00.
                    </div>
                  )}
                  {!isAdmin() && !canEdit() && (
                    <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-4 mb-2 text-amber-800 font-semibold">
                      🔒 {t.ca_formLocked}
                    </div>
                  )}
                  {chartererData.isLocked && !canEdit() && (
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-4 mb-2">
                      <div className="flex items-start gap-3">
                        <div className="text-3xl">🔒</div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-green-700 font-bold text-lg">
                              {t.ca_charterPartySubmitted}
                            </span>
                            <span className="bg-green-600 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                              {t.ca_legallyBinding}
                            </span>
                          </div>
                          <div className="text-sm text-gray-700 space-y-0.5">
                            <div>
                              <span className="font-semibold">
                                {(t.ca_submittedBy + ' ')}
                              </span>
                              {chartererData.firstName} {chartererData.lastName}
                            </div>
                            {chartererData.submittedAt && (
                              <div>
                                <span className="font-semibold">
                                  {(t.ca_submittedOn + ' ')}
                                </span>
                                {new Date(chartererData.submittedAt).toLocaleString(language)}
                              </div>
                            )}
                            {chartererData.lastModifiedAt && chartererData.lastModifiedAt !== chartererData.submittedAt && (
                              <div>
                                <span className="font-semibold">
                                  {(t.ca_lastModified + ' ')}
                                </span>
                                {new Date(chartererData.lastModifiedAt).toLocaleString(language)}
                              </div>
                            )}
                          </div>
                          <p className="text-xs text-gray-600 mt-2 italic">
                            {t.ca_toModifyNote}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                  {/* ΦΥΣΙΚΟ ΠΡΟΣΩΠΟ Ή ΕΤΑΙΡΕΙΑ — αλλάζει ΤΙ ζητείται παρακάτω. */}
                  <div className="flex gap-4 items-center mb-2" data-yms-charterer-type>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="chartererKind" value="person" checked={!chartererIsCompany}
                             aria-label={t.ca_partyPerson} disabled={!canEdit()}
                             onChange={() => setChartererKind(false)} />
                      <span>{t.ca_partyPerson}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="chartererKind" value="company" checked={chartererIsCompany}
                             aria-label={t.ca_partyCompany} disabled={!canEdit()}
                             onChange={() => setChartererKind(true)} />
                      <span>{t.ca_partyCompany}</span>
                    </label>
                  </div>
                  {chartererIsCompany && (
                    <p className="text-xs text-gray-600 mb-2 italic">{t.ca_companyNoPersonal}</p>
                  )}

                  {chartererIsCompany ? (
                    <div data-yms-charterer-company>{reqLabel(t.ca_companyName)}
                      <input type="text" placeholder={t.ca_companyName} aria-label={t.ca_companyName}
                             value={chartererData.companyName ?? chartererData.lastName ?? ''}
                             onChange={(e) => { const n = e.target.value;
                               setChartererData((prev: any) => ({ ...prev, ...companyChartererPatch(n, prev.country) })); }}
                             readOnly={!canEdit()}
                             className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                  ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>{reqLabel(t.ca_firstName)}<input type="text" placeholder={t.ca_firstName} value={chartererData.firstName} onChange={(e) => { const fn = e.target.value; editCharterer({ firstName: fn }, { firstName: toLatin(fn), name: toLatin((fn + ' ' + (chartererData.lastName || '')).trim()) }); }} readOnly={!canEdit()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                    <div>{reqLabel(t.ca_lastName)}<input type="text" placeholder={t.ca_lastName} value={chartererData.lastName} onChange={(e) => { const ln = e.target.value; editCharterer({ lastName: ln }, { lastName: toLatin(ln), name: toLatin(((chartererData.firstName || '') + ' ' + ln).trim()) }); }} readOnly={!canEdit()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                  </div>
                  )}
                  <div><label className="text-sm font-semibold text-gray-700 mb-1 block">{t.ca_address}</label><input type="text" placeholder={t.ca_address} value={chartererData.address} onChange={(e) => editCharterer({ address: e.target.value }, { address: e.target.value })} readOnly={!canEdit()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                  {/* ΧΩΡΑ — η εταιρεία δεν έχει εθνικότητα, έχει έδρα. */}
                  {chartererIsCompany && (
                    <div><label className="text-sm font-semibold text-gray-700 mb-1 block">{t.ca_country}</label>
                      <CountrySelect value={chartererData.country ?? ''}
                                     onChange={(v) => setChartererData((prev: any) => ({ ...prev, country: v }))}
                                     lang={language} placeholder={t.ca_country} disabled={!canEdit()}
                                     className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                  )}
                  {/* ΑΤΟΜΙΚΑ — μόνο για φυσικό πρόσωπο. */}
                  {!chartererIsCompany && (
                  <div data-yms-charterer-personal className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="flex flex-col">
                      {reqLabel(t.ca_dateOfBirth)}
                      <input type="date" value={chartererData.dob ?? ''} onChange={(e) => editCharterer({ dob: e.target.value }, { dateOfBirth: e.target.value })} readOnly={!canEdit()} className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />
                    </div>
                    <div className="flex flex-col">
                      {reqLabel((GEN_LABEL[language] || GEN_LABEL.en))}
                      <select value={chartererData.gender ?? ''} onChange={(e) => editCharterer({ gender: e.target.value }, { gender: e.target.value })} disabled={!canEdit()} className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none">
                        <option value="">Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    </div>
                  </div>
                  <div>{reqLabel(t.ca_nationality)}<CountrySelect value={chartererData.nationality ?? ''} onChange={(v) => editCharterer({ nationality: v }, { nationality: v })} lang={language} placeholder={t.ca_nationality} disabled={!canEdit()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                  <div className="flex gap-4 items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="idType" value="id" checked={chartererData.idType === 'id'} onChange={() => setChartererData(prev => ({ ...prev, idType: 'id' }))} />
                      <span>{t.ca_idCard}</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="idType" value="passport" checked={chartererData.idType === 'passport'} onChange={() => setChartererData(prev => ({ ...prev, idType: 'passport' }))} />
                      <span>{t.ca_passport}</span>
                    </label>
                  </div>
                  <div>{reqLabel(chartererData.idType === 'id' ? t.ca_idCardNumber : t.ca_passportNum)}<input type="text" placeholder={chartererData.idType === 'id' ? t.ca_idCardNumber : t.ca_passportNum} value={chartererData.idNumber} onChange={(e) => editCharterer({ idNumber: e.target.value }, { passport: latinId(e.target.value) })} readOnly={!canEdit()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                  </div>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input type="text" placeholder={t.ca_taxNumberAfm} value={chartererData.taxNumber} onChange={(e) => editCharterer({ taxNumber: e.target.value }, { taxNumber: e.target.value })} readOnly={!canEdit()} className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />
                    <input type="text" placeholder={t.ca_taxOfficeDoy} value={chartererData.taxOffice} onChange={(e) => editCharterer({ taxOffice: e.target.value }, { taxOffice: e.target.value })} readOnly={!canEdit()} className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>{reqLabel(t.ca_phoneShort)}<input type="tel" placeholder={t.ca_phoneShort} value={chartererData.tel} onChange={(e) => editCharterer({ tel: e.target.value }, { phone: e.target.value })} readOnly={!canEdit()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                    <div>{reqLabel(t.ca_emailLabel)}<input type="email" placeholder={t.ca_emailLabel} value={chartererData.email} onChange={(e) => editCharterer({ email: e.target.value }, { email: e.target.value })} readOnly={!canEdit()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                  </div>
                  {/* S4b: dropdown skipper (αντικαθιστά checkbox + popup) */}
                  <div>
                    <label className="text-sm font-semibold text-gray-700 mb-1 block">{(SKIPPER_MODE[language] || SKIPPER_MODE.en).label} <span className="cp-required">{REQ[language] || REQ.en}</span></label>
                    {/* ΕΜΦΑΝΙΣΗ ΜΟΝΟ: όταν αναγνωριστεί ότι ο σκίπερ ΕΙΝΑΙ ο ναυλωτής, το
                        dropdown δείχνει «Εγώ (ο ναυλωτής)». Η ΑΠΟΘΗΚΕΥΜΕΝΗ τιμή δεν αλλάζει. */}
                    <select value={skipperIsCharterer ? 'me' : skipperMode} onChange={(e) => applySkipperMode(e.target.value)} disabled={!canEditCrew()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none">
                      <option value="me">{(SKIPPER_MODE[language] || SKIPPER_MODE.en).me}</option>
                      <option value="crew">{(SKIPPER_MODE[language] || SKIPPER_MODE.en).crew}</option>
                      <option value="other">{(SKIPPER_MODE[language] || SKIPPER_MODE.en).other}</option>
                      <option value="invite">{(SKIPPER_MODE[language] || SKIPPER_MODE.en).invite}</option>
                    </select>
                  </div>
                  {skipperIsCharterer && (
                    <div className="bg-green-50 border border-green-300 rounded-lg p-3 text-green-800 text-sm">
                      <span className="font-semibold">✓ {(SKIPPER_MODE[language] || SKIPPER_MODE.en).youAreSkipper}{(bookingData as any)?.sameAsSkipper ? ' — ' + (SKIPPER_MODE[language] || SKIPPER_MODE.en).fromRegistration : ''}</span>
                      {/* ΑΝΑΓΝΩΡΙΣΜΕΝΟ (όχι δηλωμένο): λέμε ΓΙΑΤΙ το dropdown δείχνει «Εγώ»
                          ενώ στον ναύλο έχει μείνει άλλη επιλογή. Πριν ήταν κίτρινη προειδοποίηση
                          που αντέφασκε με το ίδιο το dropdown — τώρα εξηγεί, δεν αντιφάσκει. */}
                      {skipperSameAsChartererAuto && skipperMode !== 'me' && (
                        <div className="mt-1">{SAME_SKIPPER_HINT[language] || SAME_SKIPPER_HINT.en}</div>
                      )}
                    </div>
                  )}
                  {/* [Me]: το Gender του skipper ΚΡΥΒΕΤΑΙ — κληρονομείται από το Gender του
                      ναυλωτού (required) μέσω του save sync (skipperGen = charterer gender). «Μία φορά όλα». */}
                  {skipperMode === 'crew' && (
                    <div>
                      <label className="text-sm font-semibold text-gray-700 mb-1 block">{(SKIPPER_MODE[language] || SKIPPER_MODE.en).selectMember}</label>
                      <select value={crewMembers.findIndex((m: any) => m.role === 'skipper')} onChange={(e) => { const idx = Number(e.target.value); setCrewMembers((prev: any[]) => prev.map((m: any, i: number) => i === idx ? { ...m, role: 'skipper' } : (m.role === 'skipper' ? { ...m, role: 'passenger' } : m))); }} disabled={!canEditCrew()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none">
                        <option value={-1}>{(SKIPPER_MODE[language] || SKIPPER_MODE.en).selectMember}</option>
                        {crewMembers.map((m: any, i: number) => <option key={i} value={i}>{m.name || ('#' + (i + 1))}</option>)}
                      </select>
                    </div>
                  )}
                  {skipperMode === 'invite' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-800 text-sm">
                      {(SKIPPER_MODE[language] || SKIPPER_MODE.en).gotoInvite}
                    </div>
                  )}
                  <div>
                    <p className="font-semibold mb-2">{t.ca_chartererSignature}</p>
                    {!canEdit() && chartererData.signature ? (
                      <div className="border-2 border-green-400 rounded-lg bg-white p-2">
                        <img src={chartererData.signature} alt="Charterer Signature" className="max-w-full max-h-40 mx-auto" />
                        <p className="text-xs text-green-700 text-center mt-1">
                          ✓ {t.ca_digitallySigned}
                        </p>
                      </div>
                    ) : (
                      <>
                        <div className="border-2 border-gray-400 rounded-lg bg-white">
                          <canvas ref={chartererCanvasRef} width={600} height={150} className="w-full touch-none cursor-crosshair" onMouseDown={startDrawing(chartererCanvasRef, setIsDrawingCharterer)} onMouseMove={draw(chartererCanvasRef, isDrawingCharterer)} onMouseUp={stopDrawing(setIsDrawingCharterer, chartererCanvasRef, 'signature')} onMouseLeave={stopDrawing(setIsDrawingCharterer, chartererCanvasRef, 'signature')} onTouchStart={startDrawing(chartererCanvasRef, setIsDrawingCharterer)} onTouchMove={draw(chartererCanvasRef, isDrawingCharterer)} onTouchEnd={stopDrawing(setIsDrawingCharterer, chartererCanvasRef, 'signature')} />
                        </div>
                        <button type="button" onClick={clearCanvas(chartererCanvasRef, 'signature')} className="mt-2 px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm">{t.ca_clear}</button>
                      </>
                    )}
                  </div>
                  {/* Και σε mode 'crew': ο skipper ήρθε από το crew list, αλλά η φόρμα είναι
                      η ΜΟΝΗ θέση για ΑΦΜ/ΔΟΥ (Έλληνες skippers) — το crew schema δεν τα έχει.
                      Τα υπόλοιπα πεδία τα γεμίζει ο καθρέφτης παραπάνω. Και οι δύο πόρτες ανοιχτές. */}
                  {!skipperIsCharterer && (skipperMode === 'other' || skipperMode === 'crew') && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-3">
                      <p className="font-semibold text-blue-800">{t.ca_skipperDetailsDiff}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>{reqLabel(t.ca_firstName)}<input type="text" placeholder={t.ca_firstName} value={chartererData.skipperFirstName} onChange={(e) => { const fn = e.target.value; const nm = ((fn || '') + ' ' + (chartererData.skipperLastName || '')).trim(); editSkipper({ skipperFirstName: fn, skipperName: nm }, { firstName: toLatin(fn), name: toLatin(nm) }); }} readOnly={!canEditCrew()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                        <div>{reqLabel(t.ca_lastName)}<input type="text" placeholder={t.ca_lastName} value={chartererData.skipperLastName} onChange={(e) => { const ln = e.target.value; const nm = ((chartererData.skipperFirstName || '') + ' ' + (ln || '')).trim(); editSkipper({ skipperLastName: ln, skipperName: nm }, { lastName: toLatin(ln), name: toLatin(nm) }); }} readOnly={!canEditCrew()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex flex-col">
                          {reqLabel(t.ca_dateOfBirth)}
                          <input type="date" value={chartererData.skipperDob} onChange={(e) => editSkipper({ skipperDob: e.target.value }, { dateOfBirth: e.target.value })} readOnly={!canEditCrew()} className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />
                        </div>
                        <div className="flex flex-col">{reqLabel((GEN_LABEL[language] || GEN_LABEL.en))}<select value={chartererData.skipperGen} onChange={(e) => editSkipper({ skipperGen: e.target.value }, { gender: e.target.value })} disabled={!canEditCrew()} className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none">
                          <option value="">Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                        </select></div>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="radio" name="skipperIdType" value="id" checked={chartererData.skipperIdType === 'id'} onChange={() => setChartererData(prev => ({ ...prev, skipperIdType: 'id' }))} disabled={!canEditCrew()} />
                          <span>{t.ca_idCardNumber || 'ID'}</span>
                        </label>
                        <label className="flex items-center gap-1 cursor-pointer">
                          <input type="radio" name="skipperIdType" value="passport" checked={chartererData.skipperIdType === 'passport'} onChange={() => setChartererData(prev => ({ ...prev, skipperIdType: 'passport' }))} disabled={!canEditCrew()} />
                          <span>{t.ca_passport}</span>
                        </label>
                      </div>
                      <input type="text" placeholder={chartererData.skipperIdType === 'id' ? (t.ca_idCardNumber || 'ID No') : (t.ca_passportNum || 'Passport No')} value={chartererData.skipperIdNumber} onChange={(e) => editSkipper({ skipperIdNumber: e.target.value }, { passport: latinId(e.target.value) })} readOnly={!canEditCrew()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />
                      <div>{reqLabel(t.ca_nationality)}<CountrySelect value={chartererData.skipperNationality ?? ''} onChange={(v) => editSkipper({ skipperNationality: v }, { nationality: v })} lang={language} placeholder={t.ca_nationality} disabled={!canEditCrew()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" /></div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input type="text" placeholder={t.ca_taxNumberAfm} value={chartererData.skipperTaxNumber} onChange={(e) => editSkipper({ skipperTaxNumber: e.target.value }, { taxNumber: e.target.value })} readOnly={!canEditCrew()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />
                        <input type="text" placeholder={t.ca_taxOfficeDoy} value={chartererData.skipperTaxOffice} onChange={(e) => editSkipper({ skipperTaxOffice: e.target.value }, { taxOffice: e.target.value })} readOnly={!canEditCrew()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <input type="tel" placeholder={t.ca_phoneShort} value={chartererData.skipperPhone} onChange={(e) => editSkipper({ skipperPhone: e.target.value }, { phone: e.target.value })} readOnly={!canEditCrew()} className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />
                        <input type="email" placeholder={t.ca_emailLabel} value={chartererData.skipperEmail} onChange={(e) => editSkipper({ skipperEmail: e.target.value }, { email: e.target.value })} readOnly={!canEditCrew()} className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />
                      </div>
                      <input type="text" placeholder={t.ca_address} value={chartererData.skipperAddress} onChange={(e) => editSkipper({ skipperAddress: e.target.value }, { address: e.target.value })} readOnly={!canEditCrew()} className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none" />
                      <div>
                        <p className="font-semibold mb-2">{t.ca_skipperSignature}</p>
                        {!canEditCrew() && chartererData.skipperSignature ? (
                          <div className="border-2 border-green-400 rounded-lg bg-white p-2">
                            <img src={chartererData.skipperSignature} alt="Skipper Signature" className="max-w-full max-h-40 mx-auto" />
                            <p className="text-xs text-green-700 text-center mt-1">
                              ✓ {t.ca_digitallySigned}
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="border-2 border-gray-400 rounded-lg bg-white">
                              <canvas ref={skipperCanvasRef} width={600} height={150} className="w-full touch-none cursor-crosshair" onMouseDown={startDrawing(skipperCanvasRef, setIsDrawingSkipper)} onMouseMove={draw(skipperCanvasRef, isDrawingSkipper)} onMouseUp={stopDrawing(setIsDrawingSkipper, skipperCanvasRef, 'skipperSignature')} onMouseLeave={stopDrawing(setIsDrawingSkipper, skipperCanvasRef, 'skipperSignature')} onTouchStart={startDrawing(skipperCanvasRef, setIsDrawingSkipper)} onTouchMove={draw(skipperCanvasRef, isDrawingSkipper)} onTouchEnd={stopDrawing(setIsDrawingSkipper, skipperCanvasRef, 'skipperSignature')} />
                            </div>
                            <button type="button" onClick={clearCanvas(skipperCanvasRef, 'skipperSignature')} className="mt-2 px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded-lg text-sm">{t.ca_clear}</button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* S5/2a: license κάρτα αμέσως κάτω από το skipper (Boatjump)· όχι για [invitation]. */}
        {skipperMode !== 'invite' && licenseCard}

        {/* Phase 6: Invite Crew/Skipper Section */}
        <div className='bg-white rounded-2xl p-6 shadow-lg ca-section'>
          <h2 className='text-xl font-bold text-gray-800 flex items-center gap-2 mb-4'>
            <span className='text-2xl'>📧</span>
            {t.ca_inviteTitle}
          </h2>
          <p className='text-sm text-gray-600 mb-4'>
            {t.ca_inviteDesc}
          </p>
          <div className='border-2 border-gray-200 rounded-lg p-4 mb-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-3'>
              <input type='text' placeholder='First Name' value={inviteForm.firstName}
                onChange={(e) => setInviteForm(p => ({ ...p, firstName: e.target.value }))}
                className='px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none' />
              <input type='text' placeholder='Last Name' value={inviteForm.lastName}
                onChange={(e) => setInviteForm(p => ({ ...p, lastName: e.target.value }))}
                className='px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none' />
            </div>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-3 mb-3'>
              <input type='email' placeholder='Email (required)' value={inviteForm.email}
                onChange={(e) => setInviteForm(p => ({ ...p, email: e.target.value }))}
                className='px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none' />
              <input type='tel' placeholder='Phone (optional, e.g. +30 69...)' value={inviteForm.phone}
                onChange={(e) => setInviteForm(p => ({ ...p, phone: e.target.value }))}
                className='px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none' />
            </div>
            <div className='mb-3'>
              <select value={inviteForm.role} onChange={(e) => setInviteForm(p => ({ ...p, role: e.target.value }))}
                className='w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none'>
                <option value='crew'>Crew Member</option>
                <option value='passenger'>Passenger</option>
                <option value='skipper'>Skipper</option>
                <option value='co-skipper'>Co-Skipper</option>
              </select>
            </div>
            <button onClick={sendInvitation} disabled={inviteBusy || !inviteForm.email}
              className='w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-blue-800 disabled:opacity-50'>
              {inviteBusy ? t.ca_sending : ('📨 ' + t.ca_sendInvitation)}
            </button>
            {inviteMsg && <p className='text-sm mt-2 text-center'>{inviteMsg}</p>}
          </div>

          {invitations.length > 0 && (
            <div className='mt-4'>
              <h3 className='font-semibold text-gray-700 mb-3'>{t.ca_sentInvitations} ({invitations.length})</h3>
              <div className='space-y-2'>
                {invitations.map((inv: any) => (
                  <div key={inv.token} className={'flex items-center justify-between p-3 border rounded-lg ' + (inv.status === 'submitted' ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200')}>
                    <div className='flex-1'>
                      <div className='font-semibold text-sm'>{inv.invite_first_name} {inv.invite_last_name} <span className='text-xs text-gray-500'>({inv.role})</span></div>
                      <div className='text-xs text-gray-600'>{inv.invite_email}</div>
                      {inv.invite_phone && <div className='text-xs text-gray-500'>📱 {inv.invite_phone}</div>}
                      <div className='text-xs mt-1'>
                        {inv.status === 'submitted' ? <span className='text-green-700 font-semibold'>✓ {t.ca_submittedShort} {inv.submitted_at ? 'on ' + new Date(inv.submitted_at).toLocaleDateString() : ''}</span> : <span className='text-yellow-700 font-semibold'>⏳ {t.ca_pending}</span>}
                      </div>
                    </div>
                    {inv.status !== 'submitted' && (
                      <div className='flex gap-2 flex-wrap'>
                        <button onClick={() => resendInvitation(inv.token)} disabled={inviteBusy}
                          className='px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50'>{t.ca_resend}</button>
                        <button onClick={() => waInvite(inv)}
                          className='px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700'>💬 WhatsApp</button>
                        <button onClick={() => deleteInvitation(inv.token)} disabled={inviteBusy}
                          className='px-3 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50'>{t.ca_deleteBtn}</button>
                        <button onClick={() => { navigator.clipboard?.writeText(window.location.origin + '/crew-invite/' + inv.token); setInviteMsg(t.ca_linkCopied); }}
                          className='px-3 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700'>{t.ca_copyLink}</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        
{/* 👥 Crew List section */}
        <div id="cp-crew-section" className="bg-white rounded-2xl p-6 shadow-lg ca-section">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-4">
            <span className="text-2xl">👥</span>
            {t.ca_crewList}
            <span style={{color: '#dc2626', fontWeight: 'bold', marginLeft: '8px', fontSize: '0.85em'}}>ONLINE</span>
            </h2>
            
            {/* 🔥 FIX 27: Removed Option 1 (Google Drive download) - now using auto-fill in FleetManagement */}
            <div className="space-y-4">
              <div className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-400 transition-colors">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-gray-800">
                      {t.ca_fillCrewOnline}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {t.ca_fillCrewInApp}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowCrewForm(!showCrewForm)}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all shadow-md ${
                      showCrewForm 
                        ? 'bg-gradient-to-r from-red-600 to-red-700 text-white hover:from-red-700 hover:to-red-800'
                        : 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800'
                    }`}
                  >
                    {showCrewForm 
                      ? (('❌ ' + t.ca_close))
                      : (('✏️ ' + t.ca_fillNow))
                    }
                  </button>
                </div>

                {showCrewForm && (
                  <div onChangeCapture={() => { crewDirtyRef.current = true; }} className="mt-4 border-t pt-4">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm cp-crew-table">
                        <thead>
                          <tr className="text-left text-gray-600 border-b-2 border-gray-200">
                            <th className="py-2 pr-2">{t.ca_fullName}</th>
                            <th className="pr-2">{t.ca_passportNum}</th>
                            <th className="pr-2">{t.ca_dateOfBirth}</th>
                            <th className="pr-2">{t.ca_nationality}</th>
                            <th className="pr-2">{GEN_LABEL[language] || GEN_LABEL.en}</th>
                            <th className="pr-2">{t.ca_emailLabel}</th>
                            <th className="pr-2">{t.ca_phoneShort}</th>
                            <th className="pr-2">Role</th>
                            <th className="pr-2">Status</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Charterer synced row — ΜΟΝΟ όταν ο ναυλωτής ΔΕΝ είναι ήδη γραμμή crew
                              (αλλιώς διπλοεγγραφή/διπλός κυβερνήτης). edit → Charter Party */}
                          {showChartererSyntheticRow && (
                          <tr className="border-b bg-sky-50">
                            <td data-label={t.ca_fullName} className="py-2 pr-2 font-semibold">{`${chartererData.firstName || ''} ${chartererData.lastName || ''}`.trim() || '—'} <span className="text-xs text-sky-700">({t.ca_charterer || 'Charterer'})</span></td>
                            <td data-label={t.ca_passportNum} className="pr-2">{chartererData.idNumber || '—'}</td>
                            <td data-label={t.ca_dateOfBirth} className="pr-2">{formatDateEU(chartererData.dob) || '—'}</td>
                            <td data-label={t.ca_nationality} className="pr-2">{chartererData.nationality || '—'}</td>
                            <td data-label={GEN_LABEL[language] || GEN_LABEL.en} className="pr-2">{chartererData.gender || '—'}</td>
                            <td data-label={t.ca_emailLabel} className="pr-2">{chartererData.email || '—'}</td>
                            <td data-label={t.ca_phoneShort} className="pr-2">{chartererData.tel || '—'}</td>
                            <td data-label="Role" className="pr-2">{skipperIsCharterer ? t.ca_skipper : (t.ca_passenger + ' 1')}</td>
                            <td data-label="Status" className="pr-2">{chartererData.signature ? <span className="text-green-700">✓</span> : <span className="text-gray-400">⏳</span>}</td>
                            <td className="pr-2"><button onClick={editChartererRow} className="text-blue-600" title="Edit">✏️</button></td>
                          </tr>
                          )}

                          {crewMembers.map((member, index) => {
                            // Κρύβε τη γραμμή του charterer ΜΟΝΟ όταν τον δείχνει η synthetic
                            // (αλλιώς: match → η πραγματική γραμμή ΠΡΕΠΕΙ να φανεί, σημασμένη).
                            if (showChartererSyntheticRow && isChartererMember(member)) return null;
                            const isEmptyMember = !String(member.name || '').trim() && !String(member.passport || '').trim();
                            if (isEmptyMember && editingCrewIdx !== index) return null; // κενό ghost → όχι row· μόνο ανοιχτή κάρτα
                            const inv: any = crewInviteFor(member);
                            return (
                            <React.Fragment key={index}>
                              {!isEmptyMember && (
                              <tr className="border-b">
                                <td data-label={t.ca_fullName} className="py-2 pr-2">{member.name || '—'}{index === chartererCrewIdx && <span className="text-xs text-sky-700"> ({t.ca_charterer || 'Charterer'})</span>}</td>
                                <td data-label={t.ca_passportNum} className="pr-2">{member.passport || '—'}</td>
                                <td data-label={t.ca_dateOfBirth} className="pr-2">{formatDateEU(member.dateOfBirth) || '—'}</td>
                                <td data-label={t.ca_nationality} className="pr-2">{member.nationality || '—'}</td>
                                <td data-label={GEN_LABEL[language] || GEN_LABEL.en} className="pr-2">{member.gender || '—'}</td>
                                <td data-label={t.ca_emailLabel} className="pr-2">{member.email || '—'}</td>
                                <td data-label={t.ca_phoneShort} className="pr-2">{member.phone || '—'}</td>
                                <td data-label="Role" className="pr-2">{memberRoleLabel(member, index)}</td>
                                <td data-label="Status" className="pr-2 whitespace-nowrap">
                                  {inv && inv.status === 'submitted' ? <span className="text-green-700">✓</span>
                                    : inv ? <span className="inline-flex items-center gap-1"><span title="Invitation pending">🟡</span>
                                        <button onClick={() => resendInvitation(inv.token)} className="text-blue-600 text-xs underline" title={t.ca_resend}>↻</button>
                                        <button onClick={() => waInvite(inv)} className="text-green-600 text-xs" title="WhatsApp">💬</button></span>
                                    : <span className="text-gray-400">⏳</span>}
                                </td>
                                <td className="pr-2 whitespace-nowrap">
                                  <button onClick={() => setEditingCrewIdx(editingCrewIdx === index ? null : index)} className="text-blue-600 mr-2" title="Edit">✏️</button>
                                  <button onClick={() => { removeCrewMember(index); if (editingCrewIdx === index) setEditingCrewIdx(null); }} className="text-red-600" title="Remove">✕</button>
                                </td>
                              </tr>
                              )}
                              {editingCrewIdx === index && (
                                <tr className="cp-crew-edit"><td colSpan={10} className="p-0">
                                  <div className="bg-gray-50 rounded-lg p-4 space-y-3 my-2">
                                    {member.fromInvitation && (
                                      <div className="bg-green-50 border border-green-300 rounded-lg p-2 mb-2 text-sm text-green-800 flex items-center gap-2">
                                        <span>✓</span><span>Auto-filled from invitation (locked)</span>
                                        {member.signature && <span className="ml-auto text-xs bg-green-600 text-white px-2 py-0.5 rounded">Signed</span>}
                                      </div>
                                    )}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      <div>{reqLabel(t.ca_fullName)}<input type="text" placeholder={t.ca_fullName} value={member.name} onChange={(e) => handleCrewMemberChange(index, 'name', e.target.value)} readOnly={member.fromInvitation} className={'w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none ' + (member.fromInvitation ? 'bg-gray-100 cursor-not-allowed' : '')} /></div>
                                      <div>{reqLabel(t.ca_passportNum)}<input type="text" placeholder={t.ca_passportNum} value={member.passport} onChange={(e) => handleCrewMemberChange(index, 'passport', e.target.value)} readOnly={member.fromInvitation} className={'w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none ' + (member.fromInvitation ? 'bg-gray-100 cursor-not-allowed' : '')} /></div>
                                      <div className="flex flex-col">
                                        {reqLabel(t.ca_dateOfBirth)}
                                        <input type="date" value={member.dateOfBirth} onChange={(e) => handleCrewMemberChange(index, 'dateOfBirth', e.target.value)} readOnly={member.fromInvitation} className={'px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none ' + (member.fromInvitation ? 'bg-gray-100 cursor-not-allowed' : '')} />
                                      </div>
                                      <div>{reqLabel(t.ca_nationality)}<CountrySelect value={member.nationality ?? ''} onChange={(v) => handleCrewMemberChange(index, 'nationality', v)} lang={language} placeholder={t.ca_nationality} disabled={member.fromInvitation} className={'w-full px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none ' + (member.fromInvitation ? 'bg-gray-100 cursor-not-allowed' : '')} /></div>
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
                                      <input type="email" placeholder={t.ca_emailLabel} value={member.email || ''} onChange={(e) => handleCrewMemberChange(index, 'email', e.target.value)} readOnly={member.fromInvitation && !isAdmin()} className={'px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none ' + (member.fromInvitation && !isAdmin() ? 'bg-gray-100' : '')} />
                                      <input type="tel" placeholder={t.ca_phoneShort} value={member.phone || ''} onChange={(e) => handleCrewMemberChange(index, 'phone', e.target.value)} readOnly={member.fromInvitation && !isAdmin()} className={'px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none ' + (member.fromInvitation && !isAdmin() ? 'bg-gray-100' : '')} />
                                      <div className="flex flex-col">{reqLabel((GEN_LABEL[language] || GEN_LABEL.en))}<select value={member.gender || ''} onChange={(e) => handleCrewMemberChange(index, 'gender', e.target.value)} disabled={member.fromInvitation && !!member.gender && !isAdmin()} className={'px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none ' + (member.fromInvitation && !!member.gender && !isAdmin() ? 'bg-gray-100' : '')}>
                                        <option value="">Gender</option><option value="Male">Male</option><option value="Female">Female</option>
                                      </select></div>
                                    </div>
                                    <div className="mt-2 flex gap-2 items-center">
                                      <select value={member.role || 'passenger'} onChange={(e) => handleCrewMemberChange(index, 'role', e.target.value)} className="px-3 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none flex-1">
                                        <option value="skipper">{t.ca_skipper}</option><option value="co-skipper">{t.ca_coSkipper}</option><option value="passenger">{t.ca_passenger}</option><option value="crew">{t.ca_crewMember}</option>
                                      </select>
                                      <button onClick={() => setEditingCrewIdx(null)} className="cp-btn">Save</button>
                                    </div>
                                  </div>
                                </td></tr>
                              )}
                            </React.Fragment>
                          );})}
                        </tbody>
                      </table>
                    </div>

                    <button
                      onClick={() => { addCrewMember(); setEditingCrewIdx(crewMembers.length); }}
                      className="w-full mt-3 px-4 py-2 border-2 border-dashed border-blue-400 text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors"
                    >
                      + {t.ca_addCrewMember}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* S5/2a: license κάρτα μετακινήθηκε αμέσως κάτω από το skipper (δες licenseCard). */}

          {/* Charter Party Scan Upload - HIDDEN (set true to show) */}
        {false && (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6 ca-section">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-4">
            <span className="text-2xl">📋</span>
            {t.ca_charterPartyScanTitle}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {t.ca_uploadSignedCP}
          </p>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
            {charterPartyScan ? (
              <div className="space-y-4">
                {charterPartyScan.startsWith('data:application/pdf') || charterPartyScan.startsWith('data:application/vnd') || charterPartyScan.startsWith('data:application/msword') ? (
                  <div className="text-green-600 font-semibold">✅ {t.ca_docUploaded}</div>
                ) : (
                  <img src={charterPartyScan} alt="Charter Party" className="max-w-full max-h-64 mx-auto rounded-lg shadow-md" />
                )}
                <button
                  onClick={() => setCharterPartyScan('')}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
                >
                  🗑 {t.ca_remove}
                </button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <div className="p-6 border-2 border-gray-200 rounded-lg hover:bg-blue-50 transition-colors">
                  <div className="text-5xl mb-2">📁</div>
                  <p className="text-lg font-semibold text-gray-700">
                    {t.ca_uploadFile}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {t.ca_pdfWordImg}
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,.pdf,.doc,.docx"
                  className="hidden"
                  onChange={handleCharterPartyScanUpload}
                />
              </label>
            )}
          </div>
        </div>
        )}

        {/* Crew List Scan Upload - HIDDEN (set true to show) */}
        {false && (
        <div className="bg-white rounded-2xl shadow-md p-6 mb-6 ca-section">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-4">
            <span className="text-2xl">👥</span>
            {t.ca_crewListScanTitle}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {t.ca_uploadSignedCL}
          </p>
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
            {crewListScan ? (
              <div className="space-y-4">
                {crewListScan.startsWith('data:application/pdf') || crewListScan.startsWith('data:application/vnd') || crewListScan.startsWith('data:application/msword') ? (
                  <div className="text-green-600 font-semibold">✅ {t.ca_docUploaded}</div>
                ) : (
                  <img src={crewListScan} alt="Crew List" className="max-w-full max-h-64 mx-auto rounded-lg shadow-md" />
                )}
                <button
                  onClick={() => setCrewListScan('')}
                  className="px-4 py-2 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600"
                >
                  🗑 {t.ca_remove}
                </button>
              </div>
            ) : (
              <label className="cursor-pointer">
                <div className="p-6 border-2 border-gray-200 rounded-lg hover:bg-blue-50 transition-colors">
                  <div className="text-5xl mb-2">📁</div>
                  <p className="text-lg font-semibold text-gray-700">
                    {t.ca_uploadFile}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">
                    {t.ca_pdfWordImg}
                  </p>
                </div>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,.pdf,.doc,.docx"
                  className="hidden"
                  onChange={handleCrewListScanUpload}
                />
              </label>
            )}
          </div>
        </div>
        )}

        {/* S5: Σύνοψη προ-Submit (read-only· ✏️ scroll στο αντίστοιχο σημείο) */}
        {(() => { const S = SUMMARY_T[language] || SUMMARY_T.en; return (
        <div className="bg-white rounded-2xl p-6 shadow-lg ca-section">
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-4"><span className="text-2xl">📋</span>{S.title}</h2>
          <div className="flex items-start justify-between border-b pb-3 mb-3">
            <div>
              <div className="font-semibold text-gray-700">{S.charterer}</div>
              <div className="text-sm text-gray-600">{`${chartererData.firstName || ''} ${chartererData.lastName || ''}`.trim() || '—'}{chartererData.email ? ' · ' + chartererData.email : ''}</div>
              {(() => { const miss = chartererMissing(); return miss.length ? <div className="text-xs mt-0.5 text-amber-700">⏳ {miss.join(', ')}</div> : null; })()}
              <div className="text-xs mt-0.5">{chartererData.signature ? <span className="text-green-700">{S.signed}</span> : <span className="text-amber-700">{S.notSigned}</span>}</div>
            </div>
            <button onClick={editChartererRow} className="text-blue-600 text-sm" title={S.edit}>✏️</button>
          </div>
          <div className="flex items-start justify-between border-b pb-3 mb-3">
            <div>
              <div className="font-semibold text-gray-700">{S.skipper}</div>
              <div className="text-sm text-gray-600">{skipperSummaryText()}</div>
              {skipperDataPending() && <div className="text-xs mt-0.5 text-amber-700">{S.skipperPending}</div>}
              {skipperMode !== 'invite' && <div className="text-xs mt-0.5">{skipperLicense ? <span className="text-green-700">{S.licenseOk}</span> : <span className="text-amber-700">{S.licensePending}</span>}</div>}
            </div>
            <button onClick={editChartererRow} className="text-blue-600 text-sm" title={S.edit}>✏️</button>
          </div>
          <div className="border-b pb-3 mb-3">
            {(() => { const real = (crewMembers || []).filter(crewIsReal); return (<>
            <div className="flex items-center justify-between mb-2">
              <div className="font-semibold text-gray-700">{S.crew} ({real.length})</div>
              <button onClick={() => scrollToId('cp-crew-section')} className="text-blue-600 text-sm" title={S.edit}>✏️</button>
            </div>
            {real.length > 0 ? (
              <div className="overflow-x-auto"><table className="w-full text-sm cp-crew-table"><tbody>
                {real.map((m: any, i: number) => {
                  const r = m.role || 'passenger';
                  const cnt = real.slice(0, i + 1).filter((x: any) => (x.role || 'passenger') === r).length;
                  const roleLbl = r === 'skipper' ? t.ca_skipper : r === 'co-skipper' ? t.ca_coSkipper
                    : r === 'crew' ? (t.ca_crewMember + ' ' + cnt)
                    : (t.ca_passenger + ' ' + (((showChartererSyntheticRow && !skipperIsCharterer) ? 1 : 0) + cnt));
                  return (<tr key={i} className="border-b"><td data-label="Role" className="py-1 pr-2 font-medium">{roleLbl}</td><td data-label={t.ca_fullName} className="pr-2">{m.name || '—'}</td><td data-label="Passport" className="pr-2">{m.passport || '—'}</td></tr>);
                })}
              </tbody></table></div>
            ) : <div className="text-sm text-gray-500">—</div>}
            </>); })()}
          </div>
          <div>
            <div className="font-semibold text-gray-700 mb-1">{S.documents}</div>
            <ul className="text-sm space-y-0.5">
              <li>{skipperLicense ? <span className="text-green-700">{S.licenseOk}</span> : <span className="text-amber-700">{S.licensePending}</span>}</li>
              <li>{charterPartyScan ? <span className="text-green-700">✓ {S.charterPartyScan}</span> : <span className="text-gray-400">— {S.charterPartyScan}</span>}</li>
              <li>{crewListScan ? <span className="text-green-700">✓ {S.crewListScan}</span> : <span className="text-gray-400">— {S.crewListScan}</span>}</li>
            </ul>
          </div>
        </div>
        ); })()}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/home')}
              className="px-6 py-4 bg-gray-600 text-white rounded-lg font-bold text-lg hover:bg-gray-700 transition-colors min-h-[48px]"
            >
              ← {t.ca_backToHome}
            </button>
            <button
              onClick={handleSubmit}
              className="cp-btn text-lg shadow-lg min-h-[48px]"
            >
              ✅ {t.ca_submitAllDocs}
            </button>
          </div>
        </div>
      )}
    </div>

    {/* BUG 3: Skipper popup — one-shot after fresh charterer signature */}
    {/* S4b: skipper popup modal αφαιρέθηκε — αντικαταστάθηκε από το dropdown skipper. */}

    {showCharterPartyModal && (
      <div
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
        onClick={() => setShowCharterPartyModal(false)}
      >
        <div
          style={{ background: 'white', borderRadius: '14px', padding: '24px', maxWidth: '480px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
          onClick={(e) => e.stopPropagation()}
        >
          <p style={{ fontSize: '15px', lineHeight: '1.5', color: '#1f2937', marginBottom: '20px', whiteSpace: 'pre-wrap' }}>
            {FILL_ONLINE_FIRST[language] || FILL_ONLINE_FIRST.en}
          </p>
          <button
            type="button"
            onClick={() => setShowCharterPartyModal(false)}
            style={{ width: '100%', padding: '12px', background: '#0c4a6e', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 700, fontSize: '15px', cursor: 'pointer' }}
          >
            OK
          </button>
        </div>
      </div>
    )}

    </>
  );
}
